import React, { useEffect, useMemo, useRef, useState } from "react";
import { setSidebarStatus } from "actions/user";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { useHttp } from "hooks/useHttp";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import Arrow from "assets/images/leftArr.png";
import SortImg from "assets/images/sort.png";
import Loader from "components/Loader/Loader";
import { CSVLink } from "react-csv";
import Pagination from "components/Pagination/Pagination";
import Select from "react-select";
import moment from "moment";
import addNotification from "helpers/notify";
import { url, tokenRef } from "api";
import { eventTrack } from "helpers/ga4Helper";
import {
  getIssues,
  getExportContractors,
  deleteIssue,
  copyShareUrl,
  updateIssueStatus,
  updateIssuePriority,
  getIssueTags,
  getIssueFilterOptions,
  getIssuePeople,
  getAreaSignOff,
  setIssuesApprovedToResolve,
  sendIssuesToTriage,
} from "api/issue/issueApi";
import { getProjects } from "api/projects/getProjects";
import { Modal, Dropdown, Tooltip, Tag, Collapse, Select as AntSelect, Segmented, message, Button, Input, Space, Checkbox, DatePicker } from "antd";
import SortableTh, { useTableSort } from "components/Table/SortableTh";
import DateRangeFilter from "components/Coordination/DateRangeFilter";
import { rangeEmpty, rangeFromParams, describeRange } from "helpers/dateRange";
import { issueHref, openIssueInNewTab } from "helpers/issueHref";
import {
  EllipsisOutlined,
  BellOutlined,
  LoadingOutlined,
  EditOutlined,
  ShareAltOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
  MailOutlined,
  FileImageOutlined,
  BarChartOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  StopOutlined,
  WarningOutlined,
  FilterOutlined,
  TableOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  PlusOutlined,
  ExportOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  DisconnectOutlined
} from "@ant-design/icons";
import IssueAnalyticsChart from "./components/IssueAnalyticsChart";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import IssueKanbanView from "./components/IssueKanbanView";
import { IssueStatusSelect, IssuePrioritySelect, IssueDueDateSelect, IssueAssignSelect } from "components/Issue/IssueQuickActions";
import PeoplePicker, { peoplePickerOptions, filterPeopleOption, renderPeopleOption } from "components/PeoplePicker/PeoplePicker";
import { groupLabel, flatOptions } from "components/PeoplePicker/peoplePickerOptions";
import { isPastDue } from "helpers/issueDueDate";
import IssueByProjectView from "./components/IssueByProjectView";
import IssueDiffModal from "./components/IssueDiffModal";
import { BranchesOutlined, AuditOutlined } from "@ant-design/icons";
import { T, STATUS_CHIPS, PRIORITY_CHIPS } from "./theme";
import { getRememberedProject, rememberProject } from "helpers/projectSelection";
import IssueExportMenu from "components/Issue/IssueExportMenu";
import ActivityFilterSelect from "components/Coordination/ActivityFilterSelect";
import IssueFieldFilters, { emptyFieldFilters, fieldFiltersFromParams, fieldFilterParams, anyFieldFilter, FIELD_FILTER_KEYS } from "components/Issue/IssueFieldFilters";
import { listGanttItems } from "api/gantt/ganttApi";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { getDiscipline } from "api/discipline/getDiscipline";
import BulkSetFieldsForm, { bulkFieldsPayload, EMPTY_BULK_FIELDS } from "components/Issue/BulkSetFieldsForm";
import BulkContractorRateModal from "components/Issue/BulkContractorRateModal";
import { getBillableAccess } from "api/billableHours";
import ShareResourceModal from "components/Share/ShareResourceModal";
import { issueLabel } from "helpers/artifactPrefix";
import { issuePreviewSrc as previewSrc } from "helpers/issuePreview";
import { pruneFinishedIssues, useIssueListState } from "helpers/issueListPrune";
import { useSettledRows, SettledBadge, settledStyle } from "helpers/useSettledRows";
import { issuesFinished, refreshIssueLists } from "actions/issues";
import { usePreference } from "helpers/userPrefs";
import { clientToCssPx } from "helpers/pointerToStyle";
import { usePartnerTrades, tradesText } from "components/Partners/partnerTrades";
import { PartnerWithTrades } from "components/Partners/PartnerTradeLabel";
import OrphanedIssuesDrawer from "./OrphanedIssuesDrawer";
import OwnerSignOffFilter from "components/Coordination/OwnerSignOffFilter";

const { Panel } = Collapse;

// Issue icon SVG component
const IssueIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

// Status and Priority configs — colors come from the shared issue theme
const STATUS_CONFIG = {
  open: { color: STATUS_CHIPS.open.fg, bg: STATUS_CHIPS.open.bg, bd: STATUS_CHIPS.open.bd, label: "Open", icon: <ClockCircleOutlined /> },
  pending: { color: STATUS_CHIPS.pending.fg, bg: STATUS_CHIPS.pending.bg, bd: STATUS_CHIPS.pending.bd, label: "Pending", icon: <ExclamationCircleOutlined /> },
  under_review: { color: STATUS_CHIPS.pending.fg, bg: STATUS_CHIPS.pending.bg, bd: STATUS_CHIPS.pending.bd, label: "In review", icon: <ExclamationCircleOutlined /> },
  resolved: { color: STATUS_CHIPS.resolved.fg, bg: STATUS_CHIPS.resolved.bg, bd: STATUS_CHIPS.resolved.bd, label: "Resolved", icon: <CheckCircleOutlined /> },
  abandoned: { color: STATUS_CHIPS.abandoned.fg, bg: STATUS_CHIPS.abandoned.bg, bd: STATUS_CHIPS.abandoned.bd, label: "Abandoned", icon: <StopOutlined /> },
};

const PRIORITY_CONFIG = {
  urgent: { color: PRIORITY_CHIPS.urgent.fg, bg: PRIORITY_CHIPS.urgent.bg, bd: PRIORITY_CHIPS.urgent.bd, label: "Urgent", icon: <WarningOutlined /> },
  normal: { color: PRIORITY_CHIPS.normal.fg, bg: PRIORITY_CHIPS.normal.bg, bd: PRIORITY_CHIPS.normal.bd, label: "Normal", icon: null },
  low: { color: PRIORITY_CHIPS.low.fg, bg: PRIORITY_CHIPS.low.bg, bd: PRIORITY_CHIPS.low.bd, label: "Low", icon: null },
};

// WHO TOUCHED IT LAST, whichever side of the job they are on.
//
// The server resolves lastEditedBy against both collections it could
// belong to (routes/issue.js): a signed-in person - a member of the
// project or a partner with an account - comes back as `user`, and
// somebody working through a magic link comes back as `guest`. A guest
// is labelled as one, because "who has this now" reads differently when
// the answer is a person with no account.
//
// An issue nobody has edited since it was raised has no stamp at all,
// and says so rather than borrowing the author's name.
// SIGNED OFF BY OWNER/GC (owner, 2026-10-21): the client's sign-off of the
// issue's level / area / system / activity, marked by the GC
// (back/utils/ownerSignOff). Empty when nothing it sits in is signed off.
export function renderOwnerSignOff(item) {
  const o = item && item.ownerSignOff;
  if (!o) return <span style={{ color: "#cbd5e1" }}>-</span>;
  return (
    <Tooltip title={`${o.planned ? "Owner/GC sign-off planned for" : "Signed off by owner/GC on"} ${o.date} - ${o.scope}${o.by ? `, marked by ${o.by}` : ""}`}>
      <span style={{ color: o.planned ? "#3b82f6" : "#1d4ed8", fontWeight: 600, whiteSpace: "nowrap" }} data-testid="owner-signoff-cell">
        ★ {o.planned ? "Planned " : ""}{o.date}
        <span style={{ display: "block", fontWeight: 400, fontSize: 11, color: "#64748b" }}>{o.scope}</span>
      </span>
    </Tooltip>
  );
}

function renderLastEditor(item) {
  const le = item && item.lastEditedBy;
  const u = le && le.user;
  const g = le && le.guest;
  const name = u
    ? ([u.name, u.lastName].filter(Boolean).join(" ") || u.email || "Someone")
    : (g ? (g.name || g.email || "A guest") : "");
  if (!name) return <span style={{ color: T.muted }}>—</span>;
  const when = le.timestamp ? moment(le.timestamp) : null;
  const tip = [
    g ? `${name} — through a shared link` : name,
    when ? when.format("MMMM D, YYYY h:mm A") : "",
  ].filter(Boolean).join("\n");
  return (
    <Tooltip title={tip}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, maxWidth: 140 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
        {g && <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: "16px" }}>guest</Tag>}
      </span>
    </Tooltip>
  );
}

// Bulk responsibles: a user id, or `email:<addr>` for a roster contact with
// no account (POST /issue/bulk-responsibles provisions it, as before).
const bulkPersonValue = (p) => p.userId || `email:${String(p.email || "").toLowerCase()}`;

// A list-params object -> its query string, paging and empties left out.
const filterQueryString = (params) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (["index", "limit", "skip"].includes(k) || v === undefined || v === null || v === "" || v === false) continue;
    qs.append(k, v === true ? "true" : String(v));
  }
  return qs.toString();
};

// The schedule stages an area's sign-off can be read from (utils/systemTasks).
const AREA_STAGES = [
  { value: "modeling", label: "Modeling" },
  { value: "coordination", label: "Coordination" },
  { value: "shop-drawings", label: "Shop drawings" },
  { value: "install", label: "Install" },
];

// The contractors inside a saved filter's stored query, for presets saved
// before the toolbar field was remembered on its own.
function contractorFromQuery(query) {
  if (!query) return null;
  const m = /(?:^|[?&])contractor=([^&]*)/.exec(String(query));
  if (!m) return null;
  const v = decodeURIComponent(m[1] || "").trim();
  return v || null;
}

// THE CONTRACTOR FILTER, each partner with its trades beneath (owner,
// 2026-10-16). Its own component so the trades hook lives outside the page's
// long body; typing still searches the name AND the trades.
// SEVERAL AT ONCE (owner, 2026-10-22: "add multiple contractors tag, in the
// contractor filtering for issue manager ... so we can export multiple
// contractors at once"). The value is a comma-joined id list on the wire,
// which is what the list and the export already parse, and an array in the
// box. One contractor behaves exactly as it did.
function ContractorFilterSelect({ projectId, contractors, value, onChange }) {
  const trades = usePartnerTrades(projectId);
  const picked = value ? String(value).split(",").filter(Boolean) : [];
  return (
    <AntSelect
      mode="multiple"
      maxTagCount="responsive"
      placeholder="Contractor"
      allowClear
      showSearch
      optionFilterProp="search"
      optionLabelProp="text"
      value={picked}
      onChange={(v) => onChange((v || []).join(","))}
      options={(contractors || []).map((c) => {
        const name = c.companyName || "(unnamed)";
        const t = trades.tradesOf(c.companyId);
        return { value: c.companyId, text: name, search: `${name} ${tradesText(t)}`, label: <PartnerWithTrades name={name} trades={t} /> };
      })}
      style={{ minWidth: 220 }}
      popupMatchSelectWidth={false}
    />
  );
}

export default function IssuePage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = allPermissionRedux
    ?.filter((element) => companyPermissionRedux?.includes(element))
    ?.filter((i) => usersPermissionRedux?.includes(i));
  // CRUD gates (admins / companyAdmins pass). Partner employees hold
  // issueEdit only (no create); partner PMs / supervisors also create.
  const isAdminUser = !!(userInfoRedux?.admin || userInfoRedux?.companyAdmin);
  const hasPerm = (k) => isAdminUser || !!FinalPermissions?.includes(k);
  const canCreate = hasPerm(ALL_PERMISSIONS.ISSUE_CREATE);
  const canEditIssue = hasPerm(ALL_PERMISSIONS.ISSUE_EDIT);
  const canDeleteIssue = hasPerm(ALL_PERMISSIONS.ISSUE_DELETE);
  const canShareIssue = hasPerm(ALL_PERMISSIONS.ISSUE_SHARE);
  // The Review Center button: for the people RUNNING the job - a project
  // member (on the roster of the selected project, or of any project
  // when none is selected) or an admin - who can open the Review Center
  // at all. Partner-company users and shared-in viewers see the picker
  // rows flagged partner/shared, and never this button.
  const canOpenReviewCenter = hasPerm(ALL_PERMISSIONS.REVIEW_CENTER_VIEW)
    || hasPerm(ALL_PERMISSIONS.REVIEW_CENTER_APPROVE)
    || hasPerm(ALL_PERMISSIONS.ISSUE_APPROVE_RESOLUTION);

  const [rawIssues, setIssues] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  const [page, setPage] = useState(0);
  const [columnNameOptions, setColumnNameOptions] = useState([]);
  // Deep links (Team Insights, dashboards): ?projectId= ?search= ?column=
  // ?dueToday=1 ?assignee=<userId>
  const urlParams = (() => { try { return new URLSearchParams(window.location.search); } catch (e) { return new URLSearchParams(); } })();
  const [columnNameSelectValue, setColumnNameSelectValue] = useState(urlParams.get("column") ? { value: urlParams.get("column"), label: urlParams.get("column") } : "");
  const [searchValue, setSearchValue] = useState(urlParams.get("search") || "");
  // Order the list by any column the server can order by. It used to be
  // hardcoded to "last modified, newest first" and the headings above the
  // answer could not change it.
  const sorter = useTableSort("updatedAt", -1, "issues.sort");
  // The issues that have been TICKED. Held as ids rather than rows so a
  // refresh cannot leave the selection holding stale copies, and cleared
  // of anything that has scrolled off the current page.
  const [selectedIssueIds, setSelectedIssueIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  // A CONTRACTOR'S RATE ON THE TICKED ISSUES (owner, 2026-10-22). The server
  // already does this for the cost page; the button is the only new part.
  // Whether to offer it at all is the SERVER's answer, read once from
  // /billable-hours/access - canEdit there means may add or edit a rate. A
  // button that 403s is worse than no button.
  const [rateOpen, setRateOpen] = useState(false);
  const [maySetRates, setMaySetRates] = useState(false);
  useEffect(() => {
    let alive = true;
    getBillableAccess()
      .then((r) => { if (alive) setMaySetRates(!!(r && r.ok && r.canEdit)); })
      .catch(() => { if (alive) setMaySetRates(false); });
    return () => { alive = false; };
  }, []);
  const [assigneeFilter, setAssigneeFilter] = useState(urlParams.get("assignee") || "");
  // ?activity=<ganttItemId>: issues / actions linked to one schedule activity
  const [activityFilter, setActivityFilter] = useState(urlParams.get("activity") || "");
  // ── tag / who raised it / who touched it last ─────────────────────────
  //
  // The tag is the label the raiser put in front of the title, so a batch
  // raised in one pass comes back as one set - on screen and, because the
  // export takes the same filter, in one PDF. The two people filters
  // answer the questions the list could not: "what did I raise" and "what
  // has this partner been touching".
  //
  // All three are deep-linkable, so a dashboard or a colleague can hand
  // somebody the exact list rather than describe it.
  const [titleTagFilter, setTitleTagFilter] = useState(urlParams.get("titleTag") || "");
  // ONE CONTRACTOR'S WORK (owner, 2026-10-06), in place of the two whole-list
  // bulk buttons that used to sit in this toolbar. Same rule as the export:
  // issues that contractor's people RAISED, HOLD or RESOLVED, or that it
  // LOGGED HOURS on (raised/hold since 2026-10-14), decided server-side by
  // the same module, so the list and the exported document cannot disagree.
  const [contractorFilter, setContractorFilter] = useState(urlParams.get("contractor") || null);
  const [contractorOpts, setContractorOpts] = useState([]);
  const [createdByFilter, setCreatedByFilter] = useState(urlParams.get("createdBy") || "");
  const [updatedByFilter, setUpdatedByFilter] = useState(urlParams.get("updatedBy") || "");
  // AREA SIGN-OFF (owner, 2026-10-15: "filter signed off areas issues from
  // non signed off issues ... through signing off dates of the activities
  // under areas"). "signed,open,none" like the tag filter; the stage whose
  // activities count (Coordination unless picked) and the day they must be
  // signed by (today when empty). The server places each issue in an area
  // and judges it - utils/issueAreaSignOff.
  const [areaSignOffFilter, setAreaSignOffFilter] = useState(urlParams.get("areaSignOff") || "");
  const [areaStage, setAreaStage] = useState(urlParams.get("areaStage") || "coordination");
  const [areaAsOf, setAreaAsOf] = useState(urlParams.get("areaSignOffAsOf") || "");
  // Where the sign-off dates come from: "" (the schedule - old), "simulation"
  // (the latest simulated schedule - the new register), "both".
  const [areaSource, setAreaSource] = useState(urlParams.get("areaSignOffSource") || "");
  const [areaSignOff, setAreaSignOff] = useState(null);
  // SIGNED OFF BY OWNER/GC (owner, 2026-10-21): "signed,planned,not", any of
  // them - the client's sign-off the GC marked, judged as the column is.
  const [ownerSignOffFilter, setOwnerSignOffFilter] = useState(urlParams.get("ownerSignOff") || "");
  // What those three dropdowns offer: only values that are actually on
  // the issues in scope, so every option returns rows.
  const [tagOptions, setTagOptions] = useState([]);
  // LEVEL / AREA / GRID / SYSTEM / DISCIPLINE (owner, 2026-10-17), one
  // object of "A,B" strings, deep-linkable as ?level= &area= ... and carried
  // by the list, the bulk tools, saved filters and every export.
  const [fieldFilters, setFieldFiltersState] = useState(() => fieldFiltersFromParams(urlParams));
  const [fieldOptions, setFieldOptions] = useState(null);
  const fieldFiltersKey = FIELD_FILTER_KEYS.map((k) => fieldFilters[k] || "").join("|");
  const [peopleOptions, setPeopleOptions] = useState({ createdBy: [], updatedBy: [] });
  const [sortedToNewer, setSortedToNewer] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [issueToDelete, setIssueToDelete] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState(null);
  // "Today": due today or overdue, still open (server-side filter).
  const [dueTodayOnly, setDueTodayOnly] = useState(urlParams.get("dueToday") === "1" || urlParams.get("dueToday") === "true");
  // "Created today": raised today, whatever their status - the quickest
  // way back to a batch you just entered.
  const [createdTodayOnly, setCreatedTodayOnly] = useState(urlParams.get("createdToday") === "1" || urlParams.get("createdToday") === "true");
  // "Overdue": past due and still open. This used to be folded into "Due
  // today", which made that button return issues due weeks ago and never
  // let anyone ask for either one on its own.
  const [overdueOnly, setOverdueOnly] = useState(urlParams.get("overdue") === "1" || urlParams.get("overdue") === "true");
  // Date ranges — the windowed form of the three buttons above, and the
  // question they cannot answer: "raised in the week of the 3rd",
  // "everything due before we close out August". Both are server-side
  // (?createdFrom/&createdTo, ?dueFrom/&dueTo) because this list is
  // paginated — filtering the 10 rows already fetched would answer about
  // the page rather than the project. Deep-linkable, so a dashboard can
  // hand someone a window.
  const [createdRange, setCreatedRangeState] = useState(() => rangeFromParams(urlParams, "created"));
  const [dueRange, setDueRangeState] = useState(() => rangeFromParams(urlParams, "due"));
  const setCreatedRange = (r) => { setCreatedRangeState(r); setPage(0); };
  const setDueRange = (r) => { setDueRangeState(r); setPage(0); };
  // Project selection lives on this toolbar — it filters the list AND is
  // handed to /issue/new so the create form doesn't need its own picker.
  // It starts on the project remembered across the coordination suite (per
  // environment + per user) and remembers every pick.
  const [projects, setProjects] = useState([]);
  const currentUserId = userInfoRedux?._id || userInfoRedux?.id;
  // A cross-project link (?assignee=<me>&dueToday=1 from the digests, no
  // projectId) means ALL projects: the remembered one would show a single
  // project's share of the list the mail counted.
  const [projectFilter, setProjectFilterState] = useState(() => urlParams.get("projectId")
    || (urlParams.get("assignee") ? null : getRememberedProject(currentUserId)) || null);
  // One set of params for the list, the bulk tools and every export; nothing
  // without a project (the server refuses the filter without one).
  const areaSignOffParams = {
    ...(areaSignOffFilter && projectFilter
      ? { areaSignOff: areaSignOffFilter, areaStage, areaSignOffAsOf: areaAsOf || undefined, areaSignOffSource: areaSource || undefined }
      : {}),
    ...(ownerSignOffFilter && projectFilter ? { ownerSignOff: ownerSignOffFilter } : {}),
  };
  // What the filter is judged on, in the reader's words: which areas are
  // signed off for the stage and which levels are still open.
  const areaSignOffTip = !areaSignOff ? "Issues whose area is signed off by the schedule's activity sign-offs. Pick a project first." : (
    <div style={{ fontSize: 12 }}>
      <div style={{ marginBottom: 4 }}>
        An issue is placed in a scheduled area by its linked area, its grid, its area name or its tag. Signed off = the
        area's {areaSignOff.stageLabel} activity on the issue's level is signed off by {areaSignOff.asOf}. No area = it
        could not be placed in an area that has {areaSignOff.stageLabel} activities.
      </div>
      {(areaSignOff.areas || []).map((a) => (
        <div key={a.id}>
          <b>{a.name}</b> ({a.issues}): {a.levels.map((l) => `${l.level || "all levels"} ${l.signed ? `signed ${String(l.signedOffAt || "").slice(0, 10)}` : "open"}`).join(", ")}
        </div>
      ))}
      {!(areaSignOff.areas || []).length && <div>No area on this project has {areaSignOff.stageLabel} activities yet.</div>}
    </div>
  );
  // THE ORPHANED ISSUES VIEW. Opened deliberately rather than probed for
  // on every page load: the lookup is a join over the project's issues,
  // and running it unasked on a project with nothing wrong would cost
  // every visit to this page to tell most people nothing.
  const [orphansOpen, setOrphansOpen] = useState(false);
  // Sharing the issue manager with a partner. Signed-in only: the point
  // of this page is raising and resolving, which a read-only guest page
  // cannot do, so the server refuses an address that is not on the
  // project's roster (utils/resourceShare SIGNED_IN_ONLY).
  const [sharing, setSharing] = useState(false);
  const setProjectFilter = (value) => {
    setProjectFilterState(value || null);
    rememberProject(currentUserId, value || "");
  };
  // Saved filter presets — named snapshots of the filter strip, kept per
  // person on the server (helpers/userPrefs; was this browser only).
  const savedFiltersKey = `issue_saved_filters_${userInfoRedux?.id || userInfoRedux?._id || "anon"}`;
  const [savedFiltersPref, persistSavedFilters] = usePreference("issues.savedFilters", [], { legacyKey: savedFiltersKey });
  const savedFilters = Array.isArray(savedFiltersPref) ? savedFiltersPref : [];
  // "Hide abandoned" / "Hide resolved" - both ON by default, so the list
  // opens on live work rather than a history of everything ever raised.
  // Server-side filters (?hideAbandoned / ?hideResolved), remembered per
  // user: unticking to walk back through resolved work should survive
  // leaving the page. An explicit Status filter overrides both, so the
  // boxes grey out while one is set.
  const hideFiltersKey = `issue_hide_filters_${userInfoRedux?.id || userInfoRedux?._id || "anon"}`;
  const [hidePref, setHidePref] = usePreference("issues.hideFilters", {}, { legacyKey: hideFiltersKey });
  const hide = hidePref && typeof hidePref === "object" ? hidePref : {};
  const hideAbandoned = hide.hideAbandoned ?? true;
  const hideResolved = hide.hideResolved ?? true;
  // Under review = waiting on the Review Center, which has its own page.
  const hideUnderReview = hide.hideUnderReview ?? true;
  // HIDE PENDING (owner, 2026-10-22: "add hide pending next to hide under
  // review"). OFF by default, unlike the other three: pending is work that
  // has been raised and not yet picked up, which is exactly what the list is
  // usually opened to find. Hiding it by default would quietly shrink
  // everybody's list the day this ships.
  const hidePending = hide.hidePending ?? false;
  // HIDE UNAPPROVED (owner, 2026-10-26: "add a filter button to hide
  // unapproved issues ... next to hide under review, make it a checkbox").
  // OFF by default: every issue raised before approval existed is unapproved,
  // so on by default would empty most people's list the day it ships. Not
  // tied to the Status filter - approval is not a status.
  const hideUnapproved = hide.hideUnapproved ?? false;
  const setHide = (patch) => setHidePref((h) => ({ ...(h && typeof h === "object" ? h : {}), ...patch }));
  const setHideAbandonedState = (v) => setHide({ hideAbandoned: v });
  const setHideResolvedState = (v) => setHide({ hideResolved: v });
  const setHideUnderReviewState = (v) => setHide({ hideUnderReview: v });
  const setHidePendingState = (v) => setHide({ hidePending: v });
  const setHideUnapprovedState = (v) => setHide({ hideUnapproved: v });
  const setHideAbandoned = (v) => { setHideAbandonedState(v); setPage(0); };
  const setHideResolved = (v) => { setHideResolvedState(v); setPage(0); };
  const setHideUnderReview = (v) => { setHideUnderReviewState(v); setPage(0); };
  const setHidePending = (v) => { setHidePendingState(v); setPage(0); };
  const setHideUnapproved = (v) => { setHideUnapprovedState(v); setPage(0); };

  // ── issues that were finished while this list was open ───────────────
  //
  // Resolving or abandoning a row used to leave it sitting there, now
  // labelled "Resolved", until somebody reloaded the page: the three hide
  // checkboxes are applied by the SERVER, at fetch time. So the row that
  // has just been finished - here, on its own page, in the Review Center,
  // by a bulk action - is remembered in redux and every list redraws
  // without it (helpers/issueListPrune.js).
  //
  // A Status filter switches this off, and the toolbar's own hide boxes
  // decide the rest: somebody who has UNTICKED "Hide resolved" is asking
  // to see resolved issues, including the one they just resolved.
  const { finished: finishedIssues, reopened: reopenedIssues, refreshTick } = useIssueListState();
  // A row the server has just confirmed as finished keeps its place for
  // one beat with "Resolved" / "Abandoned" on it - the acknowledgement -
  // and only then leaves through the prune. Store-backed, so the kanban,
  // the table and the by-project view all agree on which row that is.
  const settled = useSettledRows("issues");
  const issues = useMemo(() => {
    const withBadge = settled.apply(rawIssues);
    const inBeat = withBadge.filter((r) => r._settled);
    const rest = pruneFinishedIssues(withBadge.filter((r) => !r._settled), finishedIssues, reopenedIssues, {
      disabled: !!statusFilter,
      keepStatuses: [
        ...(hideResolved ? [] : ["resolved"]),
        ...(hideAbandoned ? [] : ["abandoned"]),
      ],
    });
    if (!inBeat.length) return rest;
    // Keep original order: splice the badged rows back where they were.
    const keep = new Set(rest.map((r) => String(r._id)));
    return withBadge.filter((r) => r._settled || keep.has(String(r._id)));
  }, [rawIssues, finishedIssues, reopenedIssues, statusFilter, hideResolved, hideAbandoned, settled]);

  // Email a no-login issue link (guest shareToken flow) to recipients
  // picked from the project roster + partner contacts (coordination).
  const [emailLinkRow, setEmailLinkRow] = useState(null);
  const [emailLinkEmails, setEmailLinkEmails] = useState([]);
  const [emailLinkOptions, setEmailLinkOptions] = useState([]);
  const [emailLinkPermission, setEmailLinkPermission] = useState("edit");
  const [emailLinkWorking, setEmailLinkWorking] = useState(false);
  // Bulk "Assign responsible" for the filtered / searched list: a partner
  // and / or one or many people applied to every issue matching the
  // current filters (all pages), via POST /issue/bulk-responsibles.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRoster, setBulkRoster] = useState([]);
  const [bulkCompany, setBulkCompany] = useState(undefined);
  const [bulkPeople, setBulkPeople] = useState([]);
  // add = on top of who is there, replace = only these, remove = take
  // these people OFF every listed issue (they stop being notified).
  const [bulkMode, setBulkMode] = useState("add");
  const [bulkWorking, setBulkWorking] = useState(false);
  const [bulkCount, setBulkCount] = useState(null);
  const openBulkAssign = async () => {
    if (!projectFilter) return addNotification("Pick a project first - responsibles come from its partner roster", "warning");
    setBulkOpen(true); setBulkCompany(undefined); setBulkPeople([]); setBulkMode("add");
    // The count has to say what will actually be touched: the ticked
    // rows when there are any, the whole filtered list when there are not.
    setBulkCount(selectedIssueIds.length || totalRecords);
    try {
      const token = localStorage.getItem(tokenRef);
      const r = await fetch(`${url}/projects/${projectFilter}/participants/roster`, { headers: { Authorization: `Bearer ${token}` } });
      setBulkRoster(r.ok ? await r.json() : []);
    } catch (e) { setBulkRoster([]); }
  };
  const bulkPeopleOptions = () => {
    const g = bulkRoster.find((x) => String(x.companyId) === String(bulkCompany));
    const groups = g ? [g] : bulkRoster;
    const out = [];
    for (const grp of groups) for (const u of grp.users || []) out.push({ value: u.userId || `email:${String(u.email || "").toLowerCase()}`, label: `${u.name || u.email}${u.email ? ` <${u.email}>` : ""} - ${grp.label || grp.companyName}${u.userId ? "" : " · no account yet"}`, name: u.name || u.email, email: u.email });
    return out;
  };
  // THE WHOLE FILTERED LIST, AS THE SCREEN SHOWS IT. Both bulk tools act on
  // every matching issue when nothing is ticked, and both used to rebuild
  // the query by hand - without the tag, contractor, activity and created /
  // updated by filters. Filtering to XC-UTILITIES_COORD and pressing "Set
  // fields" therefore reached every issue on the project. One builder, the
  // same filters the list itself sends (data.current below).
  const bulkListParams = () => ({
    index: 0, limit: 1000, search: searchValue || undefined, column: columnNameSelectValue?.value || undefined,
    status: statusFilter || undefined, priority: priorityFilter || undefined, projectId: projectFilter || undefined,
    dueToday: dueTodayOnly || undefined, createdToday: createdTodayOnly || undefined, overdue: overdueOnly || undefined,
    createdFrom: createdRange.from || undefined, createdTo: createdRange.to || undefined,
    dueFrom: dueRange.from || undefined, dueTo: dueRange.to || undefined, assignee: assigneeFilter || undefined,
    hideAbandoned: hideAbandoned || undefined, hideResolved: hideResolved || undefined, hideUnderReview: hideUnderReview || undefined, hidePending: hidePending || undefined,
    approvedToResolve: hideUnapproved ? true : undefined,
    titleTag: titleTagFilter || undefined, createdBy: createdByFilter || undefined, updatedBy: updatedByFilter || undefined,
    contractor: contractorFilter || undefined, ganttItemId: activityFilter || undefined,
    ...fieldFilterParams(fieldFilters),
    ...areaSignOffParams,
  });
  const saveCurrentFilter = () => {
    const name = window.prompt("Name this filter preset:");
    if (!name?.trim()) return;
    const preset = {
      name: name.trim(),
      statusFilter, priorityFilter, searchValue,
      sortedToNewer, layout, hideAbandoned, hideResolved, hideUnderReview, hidePending, hideUnapproved,
      createdRange, dueRange,
      titleTagFilter, createdByFilter, updatedByFilter,
      areaSignOffFilter, areaStage, areaAsOf, areaSource, activityFilter, ownerSignOffFilter,
      fieldFilters,
      // THE CONTRACTORS, as the comma list the toolbar holds (owner,
      // 2026-10-22: saved a filter on several contractors and "it did not
      // show the same multiple contractor on the filter when i reloaded page
      // and selected filter again"). It was already inside `query`, which is
      // how the cost page honoured it - but nothing put it back in the box,
      // so the list filtered correctly while the toolbar showed nothing and
      // the next change silently dropped it.
      contractorFilter,
      // THE LIST'S OWN QUERY, so the contractor cost page can apply this
      // filter exactly as the list does (owner, 2026-10-15: "save it and pull
      // it from contractor and cost"). Paging left out; the project kept for
      // reference - the cost page applies the filter to its own project.
      projectId: projectFilter || null,
      query: filterQueryString(bulkListParams()),
    };
    persistSavedFilters([
      ...savedFilters.filter((f) => f.name !== preset.name),
      preset,
    ]);
  };
  // Bulk "Set fields": discipline, activity, area / level / grid, priority
  // and due date on the ticked rows, or on every issue matching the
  // current filters when nothing is ticked - the same reach as the
  // responsible modal, through POST /issue/bulk-actions { action: "fields" }.
  // A field left untouched is left alone on every issue; "clear" empties it.
  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [fieldsCount, setFieldsCount] = useState(null);
  const [fieldsWorking, setFieldsWorking] = useState(false);
  const [fieldsDisciplines, setFieldsDisciplines] = useState([]);
  const [fieldsActivities, setFieldsActivities] = useState([]);
  // THE TAG AND THE CONTRACTOR (owner, 2026-10-15: "add the tags in set
  // fields for bulk issues, and the dicipline, should list each contractor
  // by discipline"). `pick` is what the discipline picker returned - a
  // discipline alone, or a discipline and its contractor - and `company`
  // is the contractor, which the picker fills and the Contractor row can
  // also set on its own. Untouched (undefined / "") is left alone.
  const [fieldsRoster, setFieldsRoster] = useState([]);
  const [fieldsTags, setFieldsTags] = useState([]);
  // Tags since the owner's second message the same day ("issues, can be
  // tagged by multiple tags"): `tagMode` add / remove / replace and the list
  // it applies. Add and remove keep every tag nobody named.
  const emptyFields = EMPTY_BULK_FIELDS;
  const [bulkFields, setBulkFields] = useState(emptyFields);
  const openBulkFields = async () => {
    setBulkFields(emptyFields);
    setFieldsCount(selectedIssueIds.length || totalRecords);
    setFieldsOpen(true);
    try { const d = await getDiscipline(userInfoRedux?.company); setFieldsDisciplines(Array.isArray(d) ? d : []); } catch (e) { setFieldsDisciplines([]); }
    // Contractors and tags belong to a project: without one in the toolbar
    // the picker lists disciplines only and says why.
    if (projectFilter) {
      try {
        const r = await fetch(`${url}/projects/${projectFilter}/participants/roster`, { headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` } });
        setFieldsRoster(r.ok ? await r.json() : []);
      } catch (e) { setFieldsRoster([]); }
    } else setFieldsRoster([]);
    try { const t = await getIssueTags(projectFilter || undefined); setFieldsTags(t?.tags || []); } catch (e) { setFieldsTags([]); }
    if (projectFilter) {
      try {
        const res = await listGanttItems(projectFilter);
        const all = Array.isArray(res) ? res : res?.items || [];
        // Paths first, filter second: an ancestor removed by the filter is
        // an ancestor the walk cannot resolve (helpers/schedule/activityLabel).
        // The whole schedule: a disabled row is still a parent. The form
        // offers only live leaves (helpers/schedule/leafActivityOptions).
        setFieldsActivities(withActivityPaths(all));
      } catch (e) { setFieldsActivities([]); }
    } else setFieldsActivities([]);
  };
  const applyBulkFields = async () => {
    const value = bulkFieldsPayload(bulkFields);
    if (!Object.keys(value).length) return addNotification("Pick at least one field to set", "warning");
    setFieldsWorking(true);
    try {
      let list = issues.filter((i) => selectedIssueIds.includes(String(i._id)));
      if (!list.length) {
        const all = await getIssues(bulkListParams());
        list = all?.issues || [];
      }
      if (!list.length) throw new Error("No issues match the current filters");
      const res = await fetch(`${url}/issue/bulk-actions`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
        body: JSON.stringify({ issueIds: list.map((i) => String(i._id)), action: "fields", value }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
      const bits = [`${body.updated} issue${body.updated === 1 ? "" : "s"} updated`];
      if (body.skipped?.length) bits.push(`${body.skipped.length} left alone`);
      message.success(bits.join(" · "));
      setFieldsOpen(false);
      setSelectedIssueIds([]);
      getIssuesAPI(data);
    } catch (e) { addNotification(e.message || "Bulk update failed", "danger"); }
    finally { setFieldsWorking(false); }
  };

  const applyBulkAssign = async () => {
    if (bulkMode === "remove" && !bulkPeople.length) return addNotification("Pick the people to remove", "warning");
    if (bulkMode !== "remove" && !bulkCompany && !bulkPeople.length) return addNotification("Pick a partner and/or people", "warning");
    setBulkWorking(true);
    try {
      const token = localStorage.getItem(tokenRef);
      // Every issue matching the current filters, not just this page.
      // Ticked issues win over the filtered list: if somebody has made a
      // selection, that IS the request - applying it to everything the
      // filters match instead would be a different, much larger act.
      let list = issues.filter((i) => selectedIssueIds.includes(String(i._id)));
      if (!list.length) {
        const all = await getIssues(bulkListParams());
        list = all?.issues || [];
      }
      if (!list.length) throw new Error("No issues match the current filters");
      const opts = bulkPeopleOptions();
      const people = bulkPeople.map((v) => { const o = opts.find((x) => x.value === v); return String(v).startsWith("email:") ? { email: String(v).slice(6), name: o?.name || "" } : { userId: v }; });
      const payload = bulkMode === "remove"
        ? { issueIds: list.map((i) => i._id), removeUserIds: bulkPeople.filter((v) => !String(v).startsWith("email:")) }
        : { issueIds: list.map((i) => i._id), companyId: bulkCompany || "", people, replace: bulkMode === "replace" };
      const res = await fetch(`${url}/issue/bulk-responsibles`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
      addNotification(bulkMode === "remove"
        ? `Removed from ${body.updated} of ${body.issues} issue(s) - they will no longer be notified about them`
        : `Responsible applied to ${body.updated} of ${body.issues} issue(s)`, "success");
      setBulkOpen(false);
      getIssuesAPI(data);
    } catch (e) { addNotification(e.message || "Bulk assignment failed", "danger"); }
    finally { setBulkWorking(false); }
  };

  // Email delivery test: sends the issue email to one address without
  // touching the issue (no share, no collaborator). Users without the
  // issueShare permission get the request-access path instead.
  const [testRow, setTestRow] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [testPermission, setTestPermission] = useState("edit");
  const [testWorking, setTestWorking] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [accessRequestWorking, setAccessRequestWorking] = useState(false);
  const [accessRequested, setAccessRequested] = useState(false);
  const requestShareAccess = async () => {
    setAccessRequestWorking(true);
    try {
      const { createFeatureRequest } = await import("api/featureRequests/createFeatureRequest");
      await createFeatureRequest(userInfoRedux?.id, { companyId: userInfoRedux?.company, permission: ALL_PERMISSIONS.ISSUE_SHARE, userId: userInfoRedux?.id });
      setAccessRequested(true);
      addNotification("Access request sent to your admins (issueShare)", "success");
    } catch (e) { addNotification(e.message || "Request failed", "danger"); }
    finally { setAccessRequestWorking(false); }
  };
  const openDeliveryTest = (issue) => {
    setTestRow(issue); setTestResult(null); setTestPermission("edit");
    setTestEmail(userInfoRedux?.email || "");
  };
  const sendDeliveryTest = async () => {
    const email = String(testEmail || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return addNotification("Enter a valid email address", "warning");
    setTestWorking(true); setTestResult(null);
    try {
      const token = localStorage.getItem(tokenRef);
      const res = await fetch(`${url}/issue/${testRow._id}/send-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emails: [email], permission: testPermission, test: true }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
      const ok = (body.sent || []).includes(email);
      setTestResult({ ok, email, delivery: body.delivery, failed: body.failed || [] });
      addNotification(ok ? `Test email handed to the mail server for ${email}` : `Send failed for ${email}`, ok ? "success" : "danger");
    } catch (e) {
      setTestResult({ ok: false, email, error: e.message });
      addNotification(e.message || "Send failed", "danger");
    } finally {
      setTestWorking(false);
    }
  };
  const openEmailLink = async (issue) => {
    setEmailLinkRow(issue);
    setEmailLinkEmails([]);
    setEmailLinkPermission("edit");
    const pid = issue.projectId?._id || issue.projectId;
    try {
      const token = localStorage.getItem(tokenRef);
      const hdrs = { Authorization: `Bearer ${token}` };
      const [rosterRes, partsRes] = await Promise.all([
        fetch(`${url}/projects/${pid}/participants/roster`, { headers: hdrs }),
        fetch(`${url}/projects/${pid}/participants`, { headers: hdrs }),
      ]);
      const roster = rosterRes.ok ? await rosterRes.json() : [];
      const parts = partsRes.ok ? await partsRes.json() : [];
      // The shared picker's groups (components/PeoplePicker, owner
      // 2026-10-15): partners under their trade, people tagged by kind.
      // The link can carry edit rights, so a partner executive - a reader
      // only - is shown greyed out rather than offered.
      const groups = peoplePickerOptions(roster, { personValue: (pp) => String(pp.email || "").toLowerCase(), executives: "disable" });
      const known = new Set(flatOptions(groups).map((o) => o.value));
      // Partner rows still at "invited" are not on the roster yet.
      const invited = [];
      for (const p of parts || []) {
        for (const u of p.users || []) {
          const e = String(u.email || "").toLowerCase();
          if (e && !known.has(e)) {
            known.add(e);
            invited.push({ value: e, label: `${u.name || u.email} — ${p.companyName || "partner"}${u.userId ? "" : " (invited)"}`, search: `${u.name || ""} ${e} ${p.companyName || ""}`.toLowerCase() });
          }
        }
      }
      setEmailLinkOptions(invited.length ? [...groups, { label: "Invited partners", key: "x:invited", options: invited }] : groups);
    } catch (e) {
      setEmailLinkOptions([]);
    }
  };
  const sendEmailLink = async () => {
    if (!emailLinkEmails.length) return addNotification("Pick at least one recipient", "warning");
    setEmailLinkWorking(true);
    try {
      const token = localStorage.getItem(tokenRef);
      const res = await fetch(`${url}/issue/${emailLinkRow._id}/send-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emails: emailLinkEmails, permission: emailLinkPermission }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
      addNotification(`Issue link sent to ${body.sent.length} recipient(s)`, "success");
      setEmailLinkRow(null);
    } catch (e) {
      addNotification(e.message || "Send failed", "danger");
    } finally {
      setEmailLinkWorking(false);
    }
  };

  // Reminder emails (POST /issue/:id/remind) — nudges the assignee,
  // responsible users and guest collaborators to complete/review the issue.
  const [remindingId, setRemindingId] = useState(null);
  const [remindAllWorking, setRemindAllWorking] = useState(false);
  const OPEN_ISH_STATUSES = ["open", "pending", "under_review"];
  const isOpenIsh = (issue) => OPEN_ISH_STATUSES.includes(issue?.status);
  // Late only once the whole due DAY has gone by, and read in UTC — the
  // date is stored day-precision, so a local-zone read shows the day
  // before for anyone west of Greenwich (helpers/issueDueDate).
  const isOverdue = (issue) => isOpenIsh(issue) && isPastDue(issue?.dueDate);

  const postRemind = async (issue) => {
    const token = localStorage.getItem(tokenRef);
    const res = await fetch(`${url}/issue/${issue._id}/remind`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({}),
    });
    const body = await res.json();
    if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
    return body;
  };

  const handleRemind = async (issue) => {
    if (remindingId || remindAllWorking) return; // prevent double-fire
    setRemindingId(issue._id);
    try {
      const body = await postRemind(issue);
      message.success(`Reminder sent to ${body.sent} people`);
    } catch (e) {
      message.error(e.message || "Failed to send reminder");
    } finally {
      setRemindingId(null);
    }
  };

  const overdueIssues = issues.filter(isOverdue);

  const handleRemindAllOverdue = () => {
    const targets = issues.filter(isOverdue);
    if (!targets.length || remindAllWorking) return;
    Modal.confirm({
      title: "Remind all overdue issues",
      icon: <ExclamationCircleOutlined />,
      content: `Send a reminder email for ${targets.length} overdue open issue(s) in the current list?`,
      okText: "Send reminders",
      onOk: async () => {
        setRemindAllWorking(true);
        let reminded = 0;
        let failed = 0;
        try {
          for (const issue of targets) {
            try {
              await postRemind(issue);
              reminded++;
            } catch (e) {
              failed++;
            }
            // Small delay between calls to avoid hammering the mailer
            await new Promise((resolve) => setTimeout(resolve, 300));
          }
        } finally {
          setRemindAllWorking(false);
        }
        if (failed) {
          message.warning(`${reminded} reminded, ${failed} failed`);
        } else {
          message.success(`${reminded} reminded, 0 failed`);
        }
      },
    });
  };

  const applySavedFilter = (name) => {
    const f = savedFilters.find((x) => x.name === name);
    if (!f) return;
    setStatusFilter(f.statusFilter ?? null);
    setPriorityFilter(f.priorityFilter ?? null);
    // Presets saved before these existed default back to hiding both.
    setHideAbandonedState(f.hideAbandoned ?? true);
    setHideResolvedState(f.hideResolved ?? true);
    setHideUnderReviewState(f.hideUnderReview ?? true);
    setHidePendingState(f.hidePending ?? false);
    setHideUnapprovedState(f.hideUnapproved ?? false);
    setSearchValue(f.searchValue ?? "");
    setSortedToNewer(!!f.sortedToNewer);
    // Presets saved before the ranges existed carry none — clear rather
    // than leave the previous window silently applied under a new name.
    setCreatedRangeState(f.createdRange ?? { from: "", to: "" });
    setDueRangeState(f.dueRange ?? { from: "", to: "" });
    // Presets saved before these existed carry none - clear, rather than
    // leave a previous tag or person quietly applied under a new name.
    setTitleTagFilter(f.titleTagFilter ?? "");
    setCreatedByFilter(f.createdByFilter ?? "");
    setUpdatedByFilter(f.updatedByFilter ?? "");
    setAreaSignOffFilter(f.areaSignOffFilter ?? "");
    setAreaStage(f.areaStage || "coordination");
    setAreaAsOf(f.areaAsOf ?? "");
    setAreaSource(f.areaSource ?? "");
    setOwnerSignOffFilter(f.ownerSignOffFilter ?? "");
    setActivityFilter(f.activityFilter ?? "");
    // A preset saved before contractorFilter existed still carries the ids in
    // its query string; read them from there rather than losing them.
    setContractorFilter(f.contractorFilter ?? contractorFromQuery(f.query) ?? null);
    setFieldFiltersState({ ...emptyFieldFilters(), ...(f.fieldFilters || {}) });
    if (f.layout) setLayout(f.layout);
    setPage(0);
  };
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [updatingPriority, setUpdatingPriority] = useState(null);
  const [layout, setLayout] = useState("table");

  // Table scroller height: the shared .assestmgmt-table-div class is a
  // fixed 82vh box, but on this page it starts ~230px down (two toolbar
  // rows + breadcrumbs), so its bottom - and the last rows of a 25/50-row
  // page plus the pagination bar - ended up below the window with no way to
  // scroll to them. Size it from its real top offset instead: everything
  // that follows (pagination) stays on screen and the inner scroll reaches
  // the last row.
  const tableBoxRef = useRef(null);
  const [tableBoxHeight, setTableBoxHeight] = useState(null);
  useEffect(() => {
    if (layout !== "table") return undefined;
    const calc = () => {
      const el = tableBoxRef.current;
      if (!el) return;
      let top = 0;
      for (let n = el; n; n = n.offsetParent) top += n.offsetTop || 0; // layout px (zoom-proof)
      // window.innerHeight is RENDERED px; `top` above is layout px. The
      // helper measures rendered-over-layout rather than reading
      // --app-zoom, because reading the property is correct today and
      // silently wrong the day somebody puts a transform on an ancestor.
      const viewport = clientToCssPx(window.innerHeight, document.documentElement); // layout px, like var(--vh)*100
      const reserve = 92; // pagination bar + bottom breathing room
      setTableBoxHeight(Math.max(320, Math.floor(viewport - top - reserve)));
    };
    calc();
    const t = setTimeout(calc, 250); // after fonts / toolbar wrap settle
    window.addEventListener("resize", calc);
    return () => { clearTimeout(t); window.removeEventListener("resize", calc); };
  }, [layout, issues.length]);
  const [diffModalVisible, setDiffModalVisible] = useState(false);
  const [diffModalIssue, setDiffModalIssue] = useState(null);
  // BCF 2.1 export of the issues matching the current toolbar filters
  // (project / status / priority / search) - same endpoint as the
  // coordination dashboard's Export BCF, here without a project it
  // exports every issue the caller can see.
  const exportFilters = {
    projectId: projectFilter || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    search: searchValue || undefined,
    hideAbandoned: hideAbandoned || undefined,
    hideResolved: hideResolved || undefined,
    hideUnderReview: hideUnderReview || undefined,
    hidePending: hidePending || undefined,
    approvedToResolve: hideUnapproved ? true : undefined,
    // The point of the tag: "print the issues that share this one".
    titleTag: titleTagFilter || undefined,
    createdBy: createdByFilter || undefined,
    updatedBy: updatedByFilter || undefined,
    createdFrom: createdRange.from || undefined,
    createdTo: createdRange.to || undefined,
    dueFrom: dueRange.from || undefined,
    dueTo: dueRange.to || undefined,
    // The toolbar filters the export used to drop on the floor.
    assignee: assigneeFilter || undefined,
    // THE EXPORT FOLLOWS THE SCREEN. Exporting while the list is narrowed to
    // one contractor and getting the whole project back is the oldest bug in
    // this toolbar, and the filter line on the document would then name a
    // contractor the rows do not match.
    contractor: contractorFilter || undefined,
    ganttItemId: activityFilter || undefined,
    ...fieldFilterParams(fieldFilters),
    ...areaSignOffParams,
    dueToday: dueTodayOnly || undefined,
    createdToday: createdTodayOnly || undefined,
    overdue: overdueOnly || undefined,
  };

  const data = useRef({ index: 0, limit: 10 });

  // Every property worth searching on. Dates take YYYY, YYYY-MM or
  // YYYY-MM-DD; the issue number takes "126" or "SPX-126"; Responsible
  // covers the name, the email and the contractor label at once.
  const issueHeaders = [
    { header: "Title", value: "title" },
    { header: "Description", value: "description" },
    { header: "Issue number", value: "displayId" },
    { header: "Project", value: "projectId" },
    { header: "Source Type", value: "sourceType" },
    { header: "Status", value: "status" },
    { header: "Priority", value: "priority" },
    { header: "Category", value: "category" },
    { header: "Discipline", value: "discipline" },
    { header: "Area", value: "location.area" },
    { header: "Level", value: "location.level" },
    { header: "Grid", value: "location.grid" },
    { header: "Responsible", value: "responsible" },
    { header: "Contractor label", value: "contractorLabel" },
    { header: "Created By", value: "createdBy" },
    { header: "Created", value: "createdAt" },
    { header: "Due date", value: "dueDate" },
    { header: "Last Modified", value: "updatedAt" },
  ];

  // True once the first page of issues has ever arrived: from then on a
  // refetch keeps the rows on screen (with the Loader overlay) instead of
  // unmounting the table - which is what used to eat the settle badge.
  const loadedOnce = useRef(false);
  const { loading: apiLoading, request: getIssuesAPI } = useHttp({
    requestCallback: (params) => getIssues(params?.current),
    onLoad: (res) => {
      loadedOnce.current = true;
      setIssues(res?.issues || []);
      setTotalRecords(res?.totalCount || 0);
      setTotalPage(Math.ceil((res?.totalCount || 0) / data?.current?.limit));
    },
    onError: (res) => {
      console.log("err", res);
      addNotification("Failed to load issues", "danger");
    },
  });

  // ── LIVE UPDATES, AND THE TAB YOU CAME BACK TO ────────────────────
  //
  // Owner, 2026-10-26: the circle was "just not updating the red circle to green
  // instantly on the Issue Manager". The manager opens an issue with target=_blank,
  // so the approval is flipped in a SECOND tab and this one is holding a list it has
  // no reason to doubt. Its own bulk action refreshed, which is why that half looked
  // fine.
  //
  // TWO MECHANISMS, BECAUSE THEY COVER DIFFERENT FAILURES:
  //   the SSE stream  - two windows side by side, updating while you watch
  //   visibilitychange - switching back to this tab, AND the case the stream alone
  //                      cannot cover: a dropped or unsupported EventSource, after
  //                      which the list would otherwise be stale for ever
  // One refetch per burst either way: a bulk approve of fifty issues is one event,
  // but a flurry of edits from several people should still be one read.
  useEffect(() => {
    let timer = null;
    const soon = () => { clearTimeout(timer); timer = setTimeout(() => getIssuesAPI(data), 800); };
    let es;
    if (projectFilter) {
      try {
        const token = localStorage.getItem(tokenRef);
        es = new EventSource(`${url}/issue/events?projectId=${projectFilter}&token=${encodeURIComponent(token || "")}`);
        es.onmessage = soon;
      } catch (e) { /* no SSE - the visibility refetch below still covers it */ }
    }
    const onVisible = () => { if (document.visibilityState === "visible") soon(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      clearTimeout(timer);
      if (es) es.close();
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectFilter]);

  // ── the ticked issues ──────────────────────────────────────────────
  const visibleIssues = (issues || []).slice(0, data.current?.limit);
  const selectedSet = new Set(selectedIssueIds.map(String));
  const selectedRows = visibleIssues.filter((i) => selectedSet.has(String(i._id)));
  const allVisibleTicked = visibleIssues.length > 0 && selectedRows.length === visibleIssues.length;
  const toggleIssue = (id) => setSelectedIssueIds((cur) => (
    cur.map(String).includes(String(id))
      ? cur.filter((x) => String(x) !== String(id))
      : [...cur, String(id)]
  ));
  // Select-all covers the rows ACTUALLY on screen. Reaching past the page
  // would act on issues nobody has looked at, which is the one thing a
  // collective action must never do.
  const toggleAllVisible = () => setSelectedIssueIds(
    allVisibleTicked ? [] : visibleIssues.map((i) => String(i._id))
  );
  useEffect(() => {
    setSelectedIssueIds((cur) => {
      const onScreen = new Set((issues || []).map((i) => String(i._id)));
      const kept = cur.filter((id) => onScreen.has(String(id)));
      return kept.length === cur.length ? cur : kept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issues]);

  // ── one act, every ticked issue ────────────────────────────────────
  // The server applies the same rules it applies to one issue (evidence
  // before a resolution, the Review Center for a partner) and answers
  // with what it could NOT do, which is reported rather than swallowed.
  // APPROVED TO RESOLVE, in bulk (owner 2026-10-26). The single toggle on an
  // issue's header and this call the SAME endpoint, so the two cannot drift.
  // The mail is not sent here: the server marks the issues and one mail per
  // person goes out after a settle period listing everything they are cleared
  // on (scripts/approvalMailSweep), because twenty clicks must not be twenty
  // mails.
  const runApproval = async (approved) => {
    if (!selectedRows.length) return;
    setBulkBusy(true);
    try {
      const ids = selectedRows.map((i) => String(i._id));
      const body = await setIssuesApprovedToResolve(ids, approved);
      const n = body.changed ?? ids.length;
      message.success(n
        ? `${n} issue${n === 1 ? "" : "s"} ${approved ? "approved to resolve - the assignees are told shortly, in one email" : "no longer approved"}`
        : "Nothing changed - they were already in that state");
      setSelectedIssueIds([]);
      getIssuesAPI(data);
    } catch (e) {
      message.error(e.message || (approved ? "Could not approve the issues" : "Could not withdraw the approval"));
    } finally {
      setBulkBusy(false);
    }
  };

  // BACK TO TRIAGE, in bulk. Asks for a reason, because the contractor who
  // raised it and the next reviewer both have to know what was wrong - the
  // issue reappears in the triage queue either way.
  const runSendToTriage = () => {
    if (!selectedRows.length) return;
    let reason = "";
    Modal.confirm({
      title: `Send ${selectedRows.length} issue${selectedRows.length === 1 ? "" : "s"} back to triage?`,
      content: (
        <div>
          <p style={{ marginBottom: 6 }}>
            They go back into the Review Center&apos;s triage tab and lose their approval to resolve.
            They stay in this list, and nothing is blocked.
          </p>
          <Input.TextArea
            placeholder="Why are they going back? (optional, but the contractor sees it)"
            autoSize={{ minRows: 2, maxRows: 4 }}
            maxLength={500}
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: "Send back to triage",
      onOk: async () => {
        setBulkBusy(true);
        try {
          const body = await sendIssuesToTriage(selectedRows.map((i) => String(i._id)), reason);
          const bits = [`${body.sent} sent back to triage`];
          if (body.withdrawn) bits.push(`${body.withdrawn} pending resolution withdrawn`);
          message.success(bits.join(" · "));
          if (body.note) message.warning(body.note);
          setSelectedIssueIds([]);
          getIssuesAPI(data);
        } catch (e) {
          message.error(e.message || "Could not send them back to triage");
        } finally {
          setBulkBusy(false);
        }
      },
    });
  };

  const runBulk = async (action, value, confirmText) => {
    if (!selectedRows.length) return;
    const go = async () => {
      // Resolving asks for the hours spent on EACH issue; the server
      // applies them to every one it resolves (back/utils/issueHours).
      let hoursSpent;
      if (action === "status" && String(value) === "resolved") {
        const { askHoursSpent } = await import("components/Issue/HoursSpent");
        hoursSpent = await askHoursSpent({
          title: `Hours spent per issue (${selectedRows.length} issue${selectedRows.length === 1 ? "" : "s"})`,
          okText: "Resolve",
          help: "Applied to each ticked issue that is resolved.",
        });
        if (hoursSpent === null) return;
      }
      setBulkBusy(true);
      try {
        const res = await fetch(`${url}/issue/bulk-actions`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
          body: JSON.stringify({ issueIds: selectedRows.map((i) => String(i._id)), action, value, ...(hoursSpent ? { hoursSpent } : {}) }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
        const bits = [`${body.updated} issue${body.updated === 1 ? "" : "s"} updated`];
        if (body.pendingReview) bits.push(`${body.pendingReview} sent for review`);
        if (body.skipped?.length) bits.push(`${body.skipped.length} skipped`);
        message.success(bits.join(" · "));
        // A bulk ending takes its rows off the list now. Only the ones the
        // server actually applied: `skipped` rows kept their status, and a
        // partner's request went to the Review Center rather than ending.
        if (action === "status" && ["resolved", "abandoned"].includes(String(value)) && !body.pendingReview) {
          const skipped = new Set((body.skipped || []).map((sk) => String(sk.id)));
          dispatch(issuesFinished(
            selectedRows.map((i) => String(i._id)).filter((id) => !skipped.has(id)),
            String(value)
          ));
        }
        if (body.skipped?.length) {
          Modal.info({
            title: `${body.skipped.length} issue${body.skipped.length === 1 ? " was" : "s were"} left alone`,
            content: (
              <div style={{ maxHeight: 260, overflow: "auto" }}>
                {body.skipped.map((sk) => (
                  <div key={sk.id} style={{ fontSize: 12, marginBottom: 4 }}>
                    <b>{sk.label || sk.id}</b> — {sk.reason}
                  </div>
                ))}
              </div>
            ),
          });
        }
        setSelectedIssueIds([]);
        getIssuesAPI(data);
      } catch (e) {
        message.error(e.message || "The bulk action failed");
      } finally {
        setBulkBusy(false);
      }
    };
    if (confirmText) {
      Modal.confirm({
        title: confirmText,
        content: `${selectedRows.length} issue${selectedRows.length === 1 ? "" : "s"} are ticked.`,
        okText: "Do it", okButtonProps: { danger: action === "delete" },
        onOk: go,
      });
      return;
    }
    await go();
  };


  useEffect(() => {
    data.current = {
      ...data.current,
      index: page,
      column: columnNameSelectValue?.value,
      search: searchValue,
      sort: sorter.field,
      sort_dir: sorter.dir,
      status: statusFilter,
      priority: priorityFilter,
      projectId: projectFilter,
      dueToday: dueTodayOnly || undefined,
      createdToday: createdTodayOnly || undefined,
      overdue: overdueOnly || undefined,
      createdFrom: createdRange.from || undefined,
      createdTo: createdRange.to || undefined,
      dueFrom: dueRange.from || undefined,
      dueTo: dueRange.to || undefined,
      assignee: assigneeFilter || undefined,
      ganttItemId: activityFilter || undefined,
      hideAbandoned: hideAbandoned || undefined,
      hideResolved: hideResolved || undefined,
      hideUnderReview: hideUnderReview || undefined,
      hidePending: hidePending || undefined,
      approvedToResolve: hideUnapproved ? true : undefined,
      titleTag: titleTagFilter || undefined,
      createdBy: createdByFilter || undefined,
      updatedBy: updatedByFilter || undefined,
      contractor: contractorFilter || undefined,
      ...fieldFilterParams(fieldFilters),
      areaSignOff: undefined, areaStage: undefined, areaSignOffAsOf: undefined, areaSignOffSource: undefined, ownerSignOff: undefined,
      ...areaSignOffParams,
    };
    getIssuesAPI(data);
  }, [page, statusFilter, priorityFilter, projectFilter, dueTodayOnly, createdTodayOnly, overdueOnly, createdRange.from, createdRange.to, dueRange.from, dueRange.to, assigneeFilter, activityFilter, hideAbandoned, hideResolved, hideUnderReview, hidePending, hideUnapproved, sorter.field, sorter.dir, refreshTick, titleTagFilter, createdByFilter, updatedByFilter, contractorFilter, areaSignOffFilter, areaStage, areaAsOf, areaSource, ownerSignOffFilter, fieldFiltersKey]);

  // The counts beside the Area sign-off choices and the areas behind them,
  // for the chosen project, stage and day. Refreshed with the list.
  useEffect(() => {
    let alive = true;
    if (!projectFilter) { setAreaSignOff(null); return undefined; }
    getAreaSignOff(projectFilter, { areaStage, areaSignOffAsOf: areaAsOf || undefined, areaSignOffSource: areaSource || undefined })
      .then((r) => { if (alive) setAreaSignOff(r && r.counts ? r : null); })
      .catch(() => { if (alive) setAreaSignOff(null); });
    return () => { alive = false; };
  }, [projectFilter, areaStage, areaAsOf, areaSource, refreshTick]);

  // The partners on the chosen project, for the contractor picker. Only when a
  // project is chosen: "anyone from that contractor" is a question about that
  // project's roster, and the server refuses it without one. A project change
  // clears the choice rather than carrying another job's contractor over.
  useEffect(() => {
    let alive = true;
    if (!projectFilter) { setContractorOpts([]); setContractorFilter(null); return undefined; }
    getExportContractors(projectFilter)
      .then((list) => { if (alive) setContractorOpts(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setContractorOpts([]); });
    return () => { alive = false; };
  }, [projectFilter]);

  // Projects for the toolbar selector — the lightweight picker endpoint:
  // it lists every project the caller belongs to ({_id,title} only),
  // unlike the heavy GET /projects, which skips model-less projects and
  // hid coordination-only ones (e.g. seeded/blank projects) from here.
  const pickerUserId = userInfoRedux?._id || userInfoRedux?.id;
  useEffect(() => {
    if (!pickerUserId) return;
    fetch(`${url}/projects/picker`, {
      headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
    })
      .then((r) => r.json())
      .then((res) => {
        const list = Array.isArray(res?.projects) ? res.projects : [];
        setProjects(list);
        // Drop a remembered project that is no longer available to this user.
        setProjectFilterState((cur) => (cur && !list.some((p) => p._id === cur) ? null : cur));
      })
      .catch((err) => {
        console.error("Failed to load projects for toolbar:", err);
        setProjects([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerUserId]);
  // Options for the Tag / Created by / Updated by pickers. Scoped to the
  // selected project, and refreshed on the shared list tick so a tag used
  // for the first time this morning appears without a reload.
  useEffect(() => {
    let cancelled = false;
    getIssueTags(projectFilter || undefined)
      .then((r) => { if (!cancelled) setTagOptions(r?.tags || []); })
      .catch(() => { if (!cancelled) setTagOptions([]); });
    getIssuePeople(projectFilter || undefined)
      .then((r) => { if (!cancelled) setPeopleOptions({ createdBy: r?.createdBy || [], updatedBy: r?.updatedBy || [] }); })
      .catch(() => { if (!cancelled) setPeopleOptions({ createdBy: [], updatedBy: [] }); });
    getIssueFilterOptions(projectFilter || undefined)
      .then((r) => { if (!cancelled) setFieldOptions(r && !r.err ? r : null); })
      .catch(() => { if (!cancelled) setFieldOptions(null); });
    return () => { cancelled = true; };
  }, [projectFilter, refreshTick]);

  // A person is picked by name but searched by name OR address - which is
  // how you find "the Ferguson foreman" when you only know the email.
  const peopleSelectOptions = (list) => (list || []).map((p) => ({
    value: p.id,
    label: p.kind === "guest" ? `${p.name} (link)` : p.name,
    search: `${p.name} ${p.email || ""}`.toLowerCase(),
    title: `${p.name}${p.email ? ` — ${p.email}` : ""} · ${p.count} issue${p.count === 1 ? "" : "s"}`,
  }));

  const memberProjects = projects.filter((p) => !p.partner && !p.shared);
  const isProjectSide = isAdminUser
    || (projectFilter ? memberProjects.some((p) => String(p._id) === String(projectFilter)) : memberProjects.length > 0);
  const showReviewCenter = isProjectSide && canOpenReviewCenter;

  useEffect(() => {
    setColumnNameOptions(
      issueHeaders
        .filter((value) => value?.value !== "updatedAt")
        .map((value) => ({ value: value.value, label: value.header }))
    );
  }, []);

  const onDropdownChange = (e) => {
    if (e.value === columnNameSelectValue.value) {
      setColumnNameSelectValue({ label: "", value: "" });
    } else {
      setColumnNameSelectValue(e);
    }
  };

  const onSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const onClearFunc = () => {
    setSearchValue("");
    setColumnNameSelectValue({ label: "", value: "" });
    data.current = { ...data.current, index: 0, search: null, column: null };
    getIssuesAPI(data);
  };

  const SearchFunction = () => {
    setIssues([]);
    data.current = {
      ...data.current,
      index: 0,
      column: columnNameSelectValue.value,
      search: searchValue,
    };
    getIssuesAPI(data);
    setTotalPage(0);
    setPage(0);
  };

  const sort = () => {
    const sorted = [...rawIssues].sort((a, b) => {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      return sortedToNewer ? dateA - dateB : dateB - dateA;
    });
    setIssues(sorted);
    setSortedToNewer(!sortedToNewer);
  };

  // Issues open in a NEW tab from the manager: the list is the place
  // you come back to, and losing its filters/scroll on every open was
  // the complaint. The ID and title cells are real links as well, so a
  // right-click / middle-click works too (helpers/issueHref).
  const openIssueViewer = (issue) => openIssueInNewTab(issue);
  const openIssueEditor = (issue) => openIssueInNewTab(issue);

  const handleCopyShareLink = async (issue) => {
    try {
      await copyShareUrl(issue.shareToken);
      addNotification("Share link copied to clipboard!", "success");
    } catch (err) {
      addNotification("Failed to copy link", "danger");
    }
  };

  const handleDeleteIssue = async () => {
    if (!issueToDelete) return;
    
    try {
      await deleteIssue(issueToDelete._id);
      addNotification("Issue deleted successfully", "success");
      setDeleteModalVisible(false);
      setIssueToDelete(null);
      getIssuesAPI(data);
    } catch (err) {
      addNotification("Failed to delete issue", "danger");
    }
  };

  const confirmDelete = (issue) => {
    setIssueToDelete(issue);
    setDeleteModalVisible(true);
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.open;
    return (
      <Tag
        style={{
          padding: "4px 8px",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          color: config.color,
          backgroundColor: config.bg,
          border: `1px solid ${config.bd}`,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {config.icon}
        {config.label}
      </Tag>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
    return (
      <Tag
        style={{
          padding: "4px 8px",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          color: config.color,
          backgroundColor: config.bg,
          border: `1px solid ${config.bd}`,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {config.icon}
        {config.label}
      </Tag>
    );
  };

  // Row-level quick actions: merge status/priority locally; a
  // reassignment changes responsibles/assignee → refetch the page.
  const patchIssueRow = (issueId, patch) => {
    if (patch?.assign) { getIssuesAPI(data); return; }
    setIssues((prev) => prev.map((m) => (m._id === issueId ? { ...m, ...patch } : m)));
  };

  const handleStatusChange = async (issue, newStatus) => {
    setUpdatingStatus(issue._id);
    try {
      const r = await updateIssueStatus(issue._id, newStatus);
      // A partner's ending does not end anything: resolved, closed and
      // abandoned alike are filed as a request and the issue moves to
      // under_review. Taking the requested status on trust showed the
      // row as Resolved when it was waiting in somebody's Review Center.
      const applied = r && r.pendingReview ? "under_review" : newStatus;
      setIssues(prev => prev.map(m =>
        m._id === issue._id ? { ...m, status: applied } : m
      ));
      addNotification(
        r && r.pendingReview
          ? (r.message || "Sent to the project owner's Review Center - it takes effect once approved")
          : `Status updated to ${STATUS_CONFIG[applied]?.label || applied}`,
        "success"
      );
    } catch (err) {
      addNotification("Failed to update status", "danger");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handlePriorityChange = async (issue, newPriority) => {
    setUpdatingPriority(issue._id);
    try {
      await updateIssuePriority(issue._id, newPriority);
      setIssues(prev => prev.map(m => 
        m._id === issue._id ? { ...m, priority: newPriority } : m
      ));
      addNotification(`Priority updated to ${PRIORITY_CONFIG[newPriority].label}`, "success");
    } catch (err) {
      addNotification("Failed to update priority", "danger");
    } finally {
      setUpdatingPriority(null);
    }
  };

  const getSourceTypeBadge = (sourceType) => {
    const typeConfig = {
      docControl: { label: "2D Sheet", icon: "📄" },
      modelViewer: { label: "3D Model", icon: "🏗️" },
      blank: { label: "Blank", icon: "📝" },
    };
    const config = typeConfig[sourceType] || typeConfig.blank;
    return (
      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>{config.icon}</span>
        <span>{config.label}</span>
      </span>
    );
  };

  const getActionMenuItems = (issue) => [
    {
      key: 'view',
      icon: <EyeOutlined />,
      label: 'View',
      onClick: () => openIssueViewer(issue)
    },
    ...(canEditIssue ? [{
      key: 'edit',
      icon: <EditOutlined />,
      label: 'Edit',
      onClick: () => openIssueEditor(issue)
    }] : []),
    ...(canShareIssue ? [{
      key: 'copy',
      icon: <CopyOutlined />,
      label: 'Copy Share Link',
      onClick: () => handleCopyShareLink(issue)
    },
    {
      key: 'email-link',
      icon: <CopyOutlined />,
      label: 'Email Link…',
      onClick: () => openEmailLink(issue)
    }] : []),
    {
      key: 'test-delivery',
      icon: <MailOutlined />,
      label: canShareIssue ? 'Test email delivery…' : 'Test email delivery… (needs issueShare)',
      onClick: () => openDeliveryTest(issue)
    },
    {
      key: 'diff',
      icon: <BranchesOutlined />,
      // Only meaningful once a resolution commit was pushed from Revit /
      // Navisworks (hasDelta from the list endpoint).
      label: issue.hasDelta ? 'View Diff' : (
        <Tooltip title="No model diff yet - a diff appears once a resolution with a commit is pushed from Revit or Navisworks" placement="left">
          <span>View Diff</span>
        </Tooltip>
      ),
      disabled: !issue.hasDelta,
      onClick: () => {
        if (!issue.hasDelta) return;
        setDiffModalIssue(issue);
        setDiffModalVisible(true);
      }
    },
    {
      key: 'resolve',
      icon: <CheckCircleOutlined />,
      label: 'Review resolution',
      onClick: () => openIssueInNewTab(issue)
    },
    {
      key: 'remind',
      icon: remindingId === issue._id
        ? <LoadingOutlined spin />
        : <BellOutlined />,
      // Says who it goes to, because that is the question: the assignee,
      // every responsible, and the no-account contacts - each on a
      // personal link (magic-login for anyone without an account on this
      // project, the ordinary link for members), so a reminder never
      // lands someone on a login wall. Offered on every issue, not just
      // the open ones - a resolved issue waiting on its reviewer is
      // exactly the kind that needs chasing.
      label: isOverdue(issue) ? 'Send reminder to responsible (overdue)' : 'Send reminder to responsible',
      danger: isOverdue(issue),
      disabled: remindingId === issue._id || remindAllWorking,
      onClick: () => handleRemind(issue)
    },
    ...(canEditIssue ? [
    {
      type: 'divider'
    },
    // The two endings, one click away. They are reachable through
    // Change Status below, but "resolve this" and "call this off" are
    // the decisions people come to this menu to make, and burying them
    // one level down made the menu look like it could only shuffle an
    // issue between its live states.
    {
      key: 'mark-resolved',
      icon: <CheckCircleOutlined />,
      label: 'Mark resolved',
      disabled: issue.status === 'resolved' || updatingStatus === issue._id,
      onClick: () => handleStatusChange(issue, 'resolved'),
    },
    {
      key: 'abandon',
      icon: <StopOutlined />,
      danger: true,
      label: 'Abandon issue',
      disabled: issue.status === 'abandoned' || updatingStatus === issue._id,
      // Calling work off is not a progress report, so it asks first.
      onClick: () => {
        Modal.confirm({
          title: `Abandon ${issueLabel(issue)}?`,
          content: 'Abandoned issues are called-off work. They stay on the project and are hidden from the list by default. From a partner company this is a request: it waits in the owner\u2019s Review Center until someone there agrees.',
          okText: 'Abandon',
          okButtonProps: { danger: true },
          onOk: () => handleStatusChange(issue, 'abandoned'),
        });
      },
    },
    {
      key: 'status',
      icon: <ClockCircleOutlined />,
      label: 'Change Status',
      // "In review" is left out on purpose - it belongs to the review
      // gate, not to whoever opens this menu. Parking an issue there by
      // hand put it in a queue that only lists submissions, so no one
      // could approve or send it back (prod SPX-314, 2026-08-28).
      children: Object.entries(STATUS_CONFIG)
        .filter(([key]) => key !== 'under_review')
        .map(([key, config]) => ({
          key: `status-${key}`,
          label: <span style={{ color: config.color }}>{config.icon} {config.label}</span>,
          onClick: () => handleStatusChange(issue, key),
          disabled: issue.status === key
        }))
    },
    {
      key: 'priority',
      icon: <WarningOutlined />,
      label: 'Change Priority',
      children: Object.entries(PRIORITY_CONFIG).map(([key, config]) => ({
        key: `priority-${key}`,
        label: <span style={{ color: config.color }}>{config.icon} {config.label}</span>,
        onClick: () => handlePriorityChange(issue, key),
        disabled: issue.priority === key
      }))
    }] : []),
    ...(canDeleteIssue ? [
    {
      type: 'divider'
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: 'Delete',
      danger: true,
      onClick: () => confirmDelete(issue)
    }] : [])
  ];

  return (
    <>
      {apiLoading && <Loader />}
      <div className="asset-mgmt-div">
        <div
          className="inner-header-position"
          style={{ background: "#fff", borderBottom: `1px solid ${T.line}` }}
        >
          <div className="custom-container">
            {/* ── Row 1: title + primary actions ─────────────────────── */}
            <div
              className="d-flex align-items-center justify-content-between flex-wrap"
              style={{ gap: 8, padding: "10px 0 8px" }}
            >
              <div className="d-flex align-items-center">
                <div
                  onClick={() => dispatch(setSidebarStatus(true))}
                  className="cursor-pointer"
                >
                  <img src={Arrow} className="left-arrow-img" alt="Back" />
                </div>
                <div className="d-flex align-items-center ml-3">
                  <IssueIcon />
                  <p className="my-heading f-14 ml-2 mr-3 mb-0">Issues Manager</p>
                </div>
              </div>
              <Space size={8} wrap>
                <Button
                  icon={<BarChartOutlined />}
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  style={
                    showAnalytics
                      ? { background: T.navyD, borderColor: T.navyD, color: "#fff", borderRadius: 6 }
                      : { color: T.navyD, borderColor: T.line2, borderRadius: 6 }
                  }
                >
                  Analytics
                </Button>
                <IssueExportMenu
                  label="Export BCF / PDF / Excel / CSV"
                  filters={exportFilters}
                  tooltip={projectFilter ? "Export the filtered issues of the selected project: BCF 2.1 (.bcfzip) for Revit / Navisworks / Solibri, or a PDF register with every issue's details and comments" : "Export the filtered issues of all your projects as BCF 2.1 or as a PDF register"}
                  style={{ color: T.navyD, borderColor: T.line2, borderRadius: 6 }}
                  onDone={({ format }) => eventTrack(`Export-${format.toUpperCase()}-Action`, `Export-${format.toUpperCase()}`, userInfoRedux?.name, "+1", false, "+1")}
                />
                <Tooltip title={projectFilter
                  ? "Give a partner on this project's roster a link that signs them in to their own issues here"
                  : "Pick a project first - the issue manager is shared per project"}>
                  <Button
                    icon={<ShareAltOutlined />}
                    disabled={!projectFilter}
                    onClick={() => setSharing(true)}
                    style={{ color: T.navyD, borderColor: T.line2, borderRadius: 6 }}
                  >
                    Share
                  </Button>
                </Tooltip>
                {canCreate && (
                <Tooltip title={projectFilter ? "Import issues from PDFs (meeting minutes, weekly job reviews / look-aheads, RFI logs), Excel workbooks or CSV trackers into the selected project - map the columns, pick the rows, commit" : "Import issues from PDF / Excel / CSV: pick the project, map the columns, commit"}>
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={() => history.push(projectFilter ? `/schedule-import/${projectFilter}?mode=issues` : "/schedule-import?mode=issues")}
                    style={{ color: T.navyD, borderColor: T.line2, borderRadius: 6 }}
                  >
                    Import PDF / Excel / BCF / XML
                  </Button>
                </Tooltip>
                )}
                {canCreate && (
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => history.push("/issues/batch")}
                  title="Spreadsheet-style batch entry with CSV paste"
                  style={{ background: T.navy, borderColor: T.navy, color: "#fff", borderRadius: 6 }}
                >
                  Add multiple
                </Button>
                )}
                {canCreate && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    // Hand the toolbar's project selection to the create form
                    history.push(
                      projectFilter
                        ? `/issue/new?projectId=${projectFilter}`
                        : "/issue/new"
                    );
                    eventTrack(
                      "Create-Issue-Action",
                      "Create-Issue",
                      userInfoRedux?.name,
                      "+1",
                      false,
                      "+1"
                    );
                  }}
                  style={{ background: T.orange, borderColor: T.orange, borderRadius: 6 }}
                >
                  Create Issue
                </Button>
                )}
                {showReviewCenter && (
                <Tooltip title="Open the Review Center - partner resolutions, claims, triage, checklist completions and schedule updates waiting on you">
                  <Button
                    icon={<AuditOutlined />}
                    onClick={() => history.push(projectFilter ? `/approval-queue?projectId=${projectFilter}` : "/approval-queue")}
                    style={{ color: T.navyD, borderColor: T.navyD, borderRadius: 6, fontWeight: 600 }}
                  >
                    Review Center
                  </Button>
                </Tooltip>
                )}
              </Space>
            </div>

            {/* ── Row 2: search + filters (left) / view + bulk actions (right) ── */}
            <div
              className="d-flex align-items-center justify-content-between flex-wrap"
              style={{ gap: 8, padding: "0 0 10px" }}
            >
              <div
                className="projActionButtons d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
              >
                <Space.Compact>
                  <Input
                    value={searchValue}
                    onChange={onSearchChange}
                    onPressEnter={SearchFunction}
                    placeholder="Search issues - title, issue number, contractor, person, activity, status, location…"
                    prefix={<SearchOutlined style={{ color: T.sub }} />}
                    style={{ width: 360 }}
                  />
                  <Button
                    onClick={SearchFunction}
                    disabled={!searchValue}
                    style={{ color: searchValue ? T.navyD : undefined }}
                  >
                    Search
                  </Button>
                </Space.Compact>
                {/* Refetch the list where it stands. Reloading the browser
                    to see somebody else's new issue cost the filters, the
                    sort, the page and the scroll position every time. */}
                <Tooltip title="Reload the issues without reloading the page - keeps your filters, sort and page">
                  <Button
                    icon={<ReloadOutlined spin={apiLoading} />}
                    onClick={() => dispatch(refreshIssueLists())}
                    disabled={apiLoading}
                    style={{ color: T.navyD, borderColor: T.line2, borderRadius: 6 }}
                  />
                </Tooltip>
                {/* ONE CONTRACTOR'S WORK, where "Assign responsible" and
                    "Set fields" used to be (owner, 2026-10-06: hide those two
                    from this view "and replace it with the contractor
                    filter").
                    BOTH OF THEM ACTED ON EVERY ISSUE IN THE CURRENT FILTERED
                    LIST, which is the widest blast radius on this page. The
                    same two operations remain on the SELECTION bar below,
                    scoped to the rows actually ticked - so nothing is lost,
                    it just cannot be done to a thousand issues by one click
                    on a filter somebody else set.
                    Only with a project chosen: "anyone from that contractor"
                    is a question about that project's roster, and the server
                    refuses it without one rather than answering vaguely. */}
                {projectFilter && contractorOpts.length > 0 && (
                  <Tooltip title="Show only issues these contractors raised, are assigned or responsible on, resolved, or logged hours on. Pick several to export them together. The export follows this.">
                    <ContractorFilterSelect
                      projectId={projectFilter}
                      contractors={contractorOpts}
                      value={contractorFilter}
                      onChange={(v) => { setContractorFilter(v || null); setPage(1); }}
                    />
                  </Tooltip>
                )}
                <AntSelect
                  placeholder="Project"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={projectFilter}
                  onChange={(value) => {
                    setProjectFilter(value || null);
                    // The tag belonged to the project being left; carrying it
                    // over would filter this project by another one's label.
                    setTitleTagFilter("");
                    // Same for the contractor: a partner of the last project
                    // would empty this one's list without saying why.
                    setContractorFilter(null);
                    // And the levels / areas / grids: another job's names.
                    setFieldFiltersState(emptyFieldFilters());
                    setPage(0);
                  }}
                  style={{ width: 160 }}
                  options={projects.map((p) => ({ value: p._id, label: p.title }))}
                />
                <AntSelect
                  placeholder="Status"
                  allowClear
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value)}
                  style={{ width: 140 }}
                  options={[
                    { value: "open", label: "Open" },
                    { value: "pending", label: "Pending" },
                    { value: "resolved", label: "Resolved" },
                    { value: "abandoned", label: "Abandoned" },
                  ]}
                />
                <AntSelect
                  placeholder="Priority"
                  allowClear
                  value={priorityFilter}
                  onChange={(value) => setPriorityFilter(value)}
                  style={{ width: 140 }}
                  options={[
                    { value: "urgent", label: "Urgent" },
                    { value: "normal", label: "Normal" },
                    { value: "low", label: "Low" },
                  ]}
                />
                {/* Tag — the label the raiser put in front of the title.
                    Only tags actually in use are offered, with their
                    counts, because a filter that can return nothing is a
                    filter people stop trusting. */}
                {/* A tag belongs to a project, so this filter does too: two
                    jobs can both run a "SNAG" batch without being the same
                    batch, and a cross-project tag filter would mix them. */}
                {/* SEVERAL TAGS AT ONCE (owner, 2026-10-15: issues "can show up
                    if either or both tags searched by"). Held as "A,B" - the
                    same ?titleTag= the URL, the list and every export already
                    carry - and the server returns issues having ANY of them. */}
                <AntSelect
                  mode="multiple"
                  maxTagCount="responsive"
                  aria-label="Tag filter"
                  placeholder={projectFilter ? "Tag" : "Tag — pick a project"}
                  allowClear
                  showSearch
                  disabled={!projectFilter}
                  optionFilterProp="label"
                  value={titleTagFilter ? String(titleTagFilter).split(",").filter(Boolean) : []}
                  onChange={(v) => { setTitleTagFilter((v || []).join(",")); setPage(0); }}
                  style={{ width: 200 }}
                  title={projectFilter
                    ? "Issues carrying ANY of the picked tags on this project. Pick them, then Export BCF / PDF / Excel / CSV to print exactly that set."
                    : "Tags belong to a project — choose one in the Project box first"}
                  options={tagOptions.map((t) => ({
                    value: t.tag,
                    label: `${t.tag} (${t.count})`,
                  }))}
                  notFoundContent="No tags used on this project yet"
                />
                {/* SEVERAL PEOPLE AT ONCE (owner, 2026-10-15: "filter/search with
                    multiple options in updated by, tags, created by"). Held as
                    "id,id" like the tag filter, so the URL, the list, the bulk
                    tools and every export carry the same value; the server
                    returns issues by ANY of them (utils/issuePeopleFilter). */}
                <AntSelect
                  mode="multiple"
                  maxTagCount="responsive"
                  aria-label="Created by filter"
                  placeholder="Created by"
                  allowClear
                  showSearch
                  optionFilterProp="search"
                  value={createdByFilter ? String(createdByFilter).split(",").filter(Boolean) : []}
                  onChange={(v) => { setCreatedByFilter((v || []).join(",")); setPage(0); }}
                  style={{ width: 200 }}
                  title="Who raised the issue - pick one or several, issues by ANY of them are listed. Type to search by name or email address."
                  options={peopleSelectOptions(peopleOptions.createdBy)}
                  notFoundContent="Nobody yet"
                />
                <AntSelect
                  mode="multiple"
                  maxTagCount="responsive"
                  aria-label="Updated by filter"
                  placeholder="Updated by"
                  allowClear
                  showSearch
                  optionFilterProp="search"
                  value={updatedByFilter ? String(updatedByFilter).split(",").filter(Boolean) : []}
                  onChange={(v) => { setUpdatedByFilter((v || []).join(",")); setPage(0); }}
                  style={{ width: 200 }}
                  title="Who touched the issue last - pick one or several, issues by ANY of them are listed. Includes partners working through a shared link, marked (link)."
                  options={peopleSelectOptions(peopleOptions.updatedBy)}
                  notFoundContent="Nobody yet"
                />
                {/* THE ACTIVITY, at any level - a level, an area, a stage or one
                    row - with everything below it (owner, 2026-10-15). The
                    server widens the pick (utils/activitySubtree). */}
                <ActivityFilterSelect
                  projectId={projectFilter}
                  value={activityFilter}
                  onChange={(v) => { setActivityFilter(v || ""); setPage(0); }}
                />
                <IssueFieldFilters
                  value={fieldFilters}
                  options={fieldOptions}
                  onChange={(v) => { setFieldFiltersState(v); setPage(0); }}
                />
                {/* AREA SIGN-OFF. Three answers, never two: an issue that
                    cannot be placed in a scheduled area is "No area", not
                    "Not signed off" - nobody checked it. The stage and the
                    day appear once a choice is made. */}
                <Tooltip title={areaSignOffTip}>
                  <AntSelect
                    mode="multiple"
                    maxTagCount="responsive"
                    aria-label="Area sign-off filter"
                    placeholder={projectFilter ? "Area sign-off" : "Area sign-off - pick a project"}
                    allowClear
                    disabled={!projectFilter}
                    value={areaSignOffFilter ? String(areaSignOffFilter).split(",").filter(Boolean) : []}
                    onChange={(v) => { setAreaSignOffFilter((v || []).join(",")); setPage(0); }}
                    style={{ width: 200 }}
                    options={[
                      { value: "signed", label: `Signed off${areaSignOff ? ` (${areaSignOff.counts.signed})` : ""}` },
                      { value: "after", label: `Created after sign-off${areaSignOff && areaSignOff.counts.after != null ? ` (${areaSignOff.counts.after})` : ""}` },
                      { value: "open", label: `Not signed off${areaSignOff ? ` (${areaSignOff.counts.open})` : ""}` },
                      { value: "none", label: `No area${areaSignOff ? ` (${areaSignOff.counts.none})` : ""}` },
                    ]}
                  />
                </Tooltip>
                <OwnerSignOffFilter
                  value={ownerSignOffFilter}
                  disabled={!projectFilter}
                  onChange={(v) => { setOwnerSignOffFilter(v); setPage(0); }}
                />
                {areaSignOffFilter && projectFilter && (
                  <>
                    <AntSelect
                      aria-label="Area sign-off stage"
                      value={areaStage}
                      onChange={(v) => { setAreaStage(v || "coordination"); setPage(0); }}
                      style={{ width: 150 }}
                      title="Whose sign-off counts: the area's activity under this stage of the schedule"
                      options={AREA_STAGES}
                    />
                    {/* Old schedule / new register / both (owner, 2026-10-16). */}
                    <AntSelect
                      aria-label="Sign-off dates from"
                      value={areaSource || "schedule"}
                      onChange={(v) => { setAreaSource(v === "schedule" ? "" : v); setPage(0); }}
                      style={{ width: 190 }}
                      title="Sign-off dates from: the schedule's own sign-offs (old), the latest simulated schedule from the sign-off register (new), or both"
                      options={[
  { value: "schedule", label: "Old schedule" },
  { value: "simulation", label: "New register" },
  { value: "both", label: "Both (register wins)" },
]}
                    />
                    <DatePicker
                      aria-label="Area signed off by"
                      placeholder="Signed off by: today"
                      allowClear
                      value={areaAsOf ? moment(areaAsOf, "YYYY-MM-DD") : null}
                      onChange={(d) => { setAreaAsOf(d ? d.format("YYYY-MM-DD") : ""); setPage(0); }}
                      style={{ width: 170 }}
                      title="Count an activity as signed off only when it was signed off on or before this day. Empty = today."
                    />
                  </>
                )}
                <Tooltip title={statusFilter
                  ? "A Status filter is set - it decides what is listed, so these are ignored while it is on"
                  : "Hide abandoned issues (called-off work). On by default"}>
                  <Checkbox
                    checked={hideAbandoned}
                    disabled={!!statusFilter}
                    onChange={(e) => setHideAbandoned(e.target.checked)}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Hide abandoned
                  </Checkbox>
                </Tooltip>
                <Tooltip title={statusFilter
                  ? "A Status filter is set - it decides what is listed, so these are ignored while it is on"
                  : "Hide resolved issues - finished work. On by default"}>
                  <Checkbox
                    checked={hideResolved}
                    disabled={!!statusFilter}
                    onChange={(e) => setHideResolved(e.target.checked)}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Hide resolved
                  </Checkbox>
                </Tooltip>
                <Tooltip title={statusFilter
                  ? "A Status filter is set - it decides what is listed, so these are ignored while it is on"
                  : "Hide issues under review - waiting on the Review Center. On by default"}>
                  <Checkbox
                    checked={hideUnderReview}
                    disabled={!!statusFilter}
                    onChange={(e) => setHideUnderReview(e.target.checked)}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Hide under review
                  </Checkbox>
                </Tooltip>
                <Tooltip title="Hide issues not yet approved to resolve (red circle). Off by default">
                  <Checkbox
                    checked={hideUnapproved}
                    onChange={(e) => setHideUnapproved(e.target.checked)}
                    style={{ whiteSpace: "nowrap" }}
                    data-testid="hide-unapproved"
                  >
                    Hide unapproved
                  </Checkbox>
                </Tooltip>
                <Tooltip title={statusFilter
                  ? "A Status filter is set - it decides what is listed, so these are ignored while it is on"
                  : "Hide issues still pending - raised and not picked up yet. Off by default"}>
                  <Checkbox
                    checked={hidePending}
                    disabled={!!statusFilter}
                    onChange={(e) => setHidePending(e.target.checked)}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Hide pending
                  </Checkbox>
                </Tooltip>
                <Select
                  options={columnNameOptions}
                  onChange={onDropdownChange}
                  value={columnNameSelectValue}
                  className="column-select"
                  placeholder="Column"
                  isClearable
                  styles={{
                    container: (base) => ({ ...base, width: 150 }),
                    control: (base) => ({
                      ...base,
                      minHeight: 32,
                      borderRadius: 6,
                      borderColor: "#d9d9d9",
                      fontSize: 14,
                    }),
                    dropdownIndicator: (base) => ({ ...base, padding: 4 }),
                    clearIndicator: (base) => ({ ...base, padding: 4 }),
                  }}
                />
                <AntSelect
                  placeholder="Saved filters"
                  allowClear
                  style={{ width: 150 }}
                  value={undefined}
                  onChange={(name) => name && applySavedFilter(name)}
                  options={savedFilters.map((f) => ({ value: f.name, label: f.name }))}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div style={{ borderTop: `1px solid ${T.line}`, padding: 6, display: "flex", gap: 6 }}>
                        <a style={{ fontSize: 12, color: T.navyD }} onClick={saveCurrentFilter}>+ Save current</a>
                        {savedFilters.length > 0 && (
                          <a style={{ fontSize: 12, color: T.removed2 }}
                             onClick={() => persistSavedFilters([])}>Clear all</a>
                        )}
                      </div>
                    </>
                  )}
                />
                <Button type={createdTodayOnly ? "primary" : "default"} onClick={() => setCreatedTodayOnly((v) => !v)}
                        title="Raised today, whatever their status">
                  Created today
                </Button>
                <Button type={dueTodayOnly ? "primary" : "default"} onClick={() => setDueTodayOnly((v) => !v)}
                        title="Due today, still open - in your timezone, not the server's">
                  Due today
                </Button>
                <Button type={overdueOnly ? "primary" : "default"} onClick={() => setOverdueOnly((v) => !v)}
                        title="Past its due date and still open">
                  Overdue
                </Button>
                <DateRangeFilter
                  label="Created"
                  value={createdRange}
                  onChange={setCreatedRange}
                  title="Raised between these two days, inclusive — in your timezone, not the server's. Leave either end empty for an open window."
                />
                <DateRangeFilter
                  label="Due"
                  value={dueRange}
                  onChange={setDueRange}
                  title="Due between these two days, inclusive. Issues with no due date are left out."
                />
                {assigneeFilter && <Tag closable onClose={() => setAssigneeFilter("")} color="blue">assigned to one person</Tag>}
                {(searchValue || statusFilter || priorityFilter || projectFilter || dueTodayOnly || createdTodayOnly || overdueOnly || !rangeEmpty(createdRange) || !rangeEmpty(dueRange) || assigneeFilter || activityFilter || titleTagFilter || createdByFilter || updatedByFilter || areaSignOffFilter || ownerSignOffFilter || anyFieldFilter(fieldFilters) || !hideAbandoned || !hideResolved || !hideUnderReview) && (
                  <Button
                    type="text"
                    onClick={() => {
                      onClearFunc();
                      setStatusFilter(null);
                      setPriorityFilter(null);
                      setDueTodayOnly(false);
                      setCreatedTodayOnly(false);
                      setOverdueOnly(false);
                      setCreatedRangeState({ from: "", to: "" });
                      setDueRangeState({ from: "", to: "" });
                      setAssigneeFilter("");
                      setActivityFilter("");
                      setTitleTagFilter("");
                      setCreatedByFilter("");
                      setUpdatedByFilter("");
                      setAreaSignOffFilter("");
                      setOwnerSignOffFilter("");
                      setFieldFiltersState(emptyFieldFilters());
                      setAreaSource("");
                      setAreaAsOf("");
                      setHideAbandoned(true);
                      setHideResolved(true);
                      setHideUnderReview(true);
                      setProjectFilterState(null); // page-local clear; keeps the shared memory
                    }}
                    style={{ color: T.muted }}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  onClick={sort}
                  icon={
                    <img
                      src={SortImg}
                      alt=""
                      style={{ width: 14, verticalAlign: "middle" }}
                    />
                  }
                  style={{ color: T.navyD, borderColor: T.line2, borderRadius: 6 }}
                >
                  Sort
                </Button>
              </div>
              <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: 8 }}
              >
                <Segmented
                  value={layout}
                  onChange={(v) => setLayout(v)}
                  options={[
                    { value: "table", icon: <TableOutlined />, label: "Table" },
                    { value: "kanban", icon: <AppstoreOutlined />, label: "Kanban" },
                    { value: "byProject", icon: <UnorderedListOutlined />, label: "By Project" },
                  ]}
                />
                <Tooltip title={projectFilter
                  ? "Issues whose schedule activity has been deleted, grouped by the activity they came from, so each pile can be moved onto the row that replaced it"
                  : "Pick a project first - orphaned issues are listed per project"}>
                  <Button
                    icon={<DisconnectOutlined />}
                    disabled={!projectFilter}
                    onClick={() => setOrphansOpen(true)}
                    style={{ borderRadius: 6 }}
                  >
                    Orphaned activities
                  </Button>
                </Tooltip>
                <Button
                  danger
                  icon={<BellOutlined />}
                  onClick={handleRemindAllOverdue}
                  disabled={remindAllWorking || overdueIssues.length === 0}
                  title="Email a reminder for every overdue open issue in the current list"
                  style={{ borderRadius: 6, opacity: remindAllWorking ? 0.6 : 1 }}
                >
                  {remindAllWorking
                    ? "Reminding..."
                    : `Remind all overdue${overdueIssues.length ? ` (${overdueIssues.length})` : ""}`}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "12px 24px 0", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <CoordinationBreadcrumbs current="Issues Manager" />
          {/* WHAT THE FILTERS ACTUALLY LEFT (owner, 2026-10-22: "show the
              final filtering result count on top of the header section of
              the issues row table, just after Coordination"). The number is
              the SERVER's total for the current filters - res.totalCount -
              not the length of the page on screen, which is at most one
              page and would read as "25 issues" on a project with 800. */}
          <span
            data-testid="issue-result-count"
            title="Issues matching every filter now set. Not the rows on this page."
            style={{ fontSize: 12.5, color: "#6B7C86", whiteSpace: "nowrap" }}
          >
            {apiLoading
              ? "counting..."
              : `${totalRecords} issue${totalRecords === 1 ? "" : "s"}`}
          </span>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div style={{ padding: "16px 24px", backgroundColor: T.bg, borderBottom: `1px solid ${T.line}` }}>
            <IssueAnalyticsChart compact={true} />
          </div>
        )}

        {layout === "kanban" && (
          <IssueKanbanView
            issues={issues}
            onOpenViewer={openIssueViewer}
            getActionMenuItems={getActionMenuItems}
            apiLoading={apiLoading}
            onStatusChange={handleStatusChange}
            canDrag={canEditIssue}
          />
        )}

        {layout === "byProject" && (
          <IssueByProjectView
            issues={issues}
            onOpenViewer={openIssueViewer}
            getActionMenuItems={getActionMenuItems}
            apiLoading={apiLoading}
          />
        )}

        {/* ── what to do with the ticked issues ──────────────────────
            It appears only when something is ticked, and it says how many
            it is about to touch, because a collective action that does
            not tell you its size is how twenty issues get resolved by
            somebody who meant three. */}
        {layout === "table" && selectedRows.length > 0 && (
          <div
            className="mt-3 d-flex align-items-center"
            style={{
              gap: 8, flexWrap: "wrap", padding: "8px 12px", borderRadius: 8,
              background: "#EEF4FF", border: "1px solid #C7D8FF",
            }}
          >
            <b style={{ fontSize: 13 }}>
              {selectedRows.length} issue{selectedRows.length === 1 ? "" : "s"} selected
            </b>
            <AntSelect
              size="small" style={{ width: 150 }} value={undefined} placeholder="Set status"
              disabled={bulkBusy}
              onChange={(v) => runBulk("status", v, `Set the status to "${String(v).replace(/_/g, " ")}"?`)}
              // No "Under review": the review gate puts issues there and
              // a reviewer takes them out. Setting it by hand - twenty at
              // a time here - filed nothing for anyone to answer.
              options={[
                { value: "open", label: "Open" },
                { value: "pending", label: "Pending" },
                { value: "resolved", label: "Resolved" },
                { value: "abandoned", label: "Abandoned" },
              ]}
            />
            <AntSelect
              size="small" style={{ width: 140 }} value={undefined} placeholder="Set priority"
              disabled={bulkBusy}
              onChange={(v) => runBulk("priority", v)}
              options={[
                { value: "urgent", label: "Urgent" },
                { value: "normal", label: "Normal" },
                { value: "low", label: "Low" },
              ]}
            />
            <DatePicker
              size="small" placeholder="Set due date" disabled={bulkBusy}
              onChange={(d) => runBulk("dueDate", d ? d.toISOString() : "")}
            />
            <Button size="small" disabled={bulkBusy} onClick={() => runBulk("dueDate", "")}>
              Clear due date
            </Button>
            {/* Responsible goes through the picker that already exists -
                same modal, same endpoint, scoped to the ticked rows. */}
            <Button size="small" disabled={bulkBusy} onClick={openBulkAssign}>
              Responsible…
            </Button>
            <Button size="small" disabled={bulkBusy} onClick={openBulkFields}>
              Set fields…
            </Button>
            {/* The circle beside the id is what these change. Advisory - nothing
                is blocked either way. */}
            <Button size="small" disabled={bulkBusy} onClick={() => runApproval(true)} data-testid="bulk-approve">
              Approve to resolve
            </Button>
            <Button size="small" disabled={bulkBusy} onClick={() => runApproval(false)} data-testid="bulk-unapprove">
              Withdraw approval
            </Button>
            <Button size="small" disabled={bulkBusy} onClick={runSendToTriage} data-testid="bulk-to-triage">
              Back to triage…
            </Button>
            {/* ONE CONTRACTOR'S RATE ON THE TICKED ISSUES (owner, 2026-10-22).
                Only for people the cost page would let edit rates, and only
                with a project picked - the contractor list is a project's
                roster, and a rate without a contractor is not a rate. */}
            {maySetRates && projectFilter ? (
              <Tooltip title={selectedIssueIds.length
                ? "Price one contractor's hours on the ticked issues. Everyone else's time on them is left alone."
                : "Tick the issues first"}>
                <Button
                  size="small"
                  disabled={bulkBusy || !selectedIssueIds.length}
                  onClick={() => setRateOpen(true)}
                >
                  Contractor rate…
                </Button>
              </Tooltip>
            ) : null}
            <Button
              size="small" danger disabled={bulkBusy}
              onClick={() => runBulk("delete", true, "Delete the selected issues?")}
            >
              Delete
            </Button>
            <Button size="small" type="text" onClick={() => setSelectedIssueIds([])} style={{ marginLeft: "auto" }}>
              Clear selection
            </Button>
          </div>
        )}

        {layout === "table" && (
        <div className="assestmgmt-table-div mt-3" ref={tableBoxRef}
             style={tableBoxHeight ? { height: tableBoxHeight, overflowY: "auto" } : undefined}>
          <table className="bid-table bid-table-small border-top-none sticky-table-header">
            <thead>
              <tr>
                {/* The issue's own identifier leads the row: it is what
                    people quote in email, on site and in the plugin -
                    a running row number identifies nothing. */}
                {/* Tick rows to act on them together. Only the rows on
                    screen - see toggleAllVisible. */}
                <th style={{ width: 34 }}>
                  <input
                    type="checkbox"
                    checked={allVisibleTicked}
                    onChange={toggleAllVisible}
                    title={allVisibleTicked ? "Clear the selection" : "Select every issue on screen"}
                  />
                </th>
                <SortableTh field="displayId" numeric style={{ width: 90 }} {...sorter.props}>ID</SortableTh>
                {/* APPROVED TO RESOLVE - green when somebody cleared this issue to be
                    worked on, red when nobody has (owner 2026-10-26). ADVISORY: it
                    blocks nothing, so the column is a signal and not a lock. Beside
                    the id because that is where a reader scanning the list looks. */}
                <SortableTh field="approvedToResolve" numeric style={{ width: 46 }} {...sorter.props}>
                  <span title="Approved to resolve">OK</span>
                </SortableTh>
                <th style={{ width: 100 }}>Preview</th>
                <SortableTh field="title" {...sorter.props}>Title</SortableTh>
                {/* The project, the author and the last editor are joined
                    AFTER the match, so the server orders those by the
                    joined name rather than by the id - see JOINED_SORTS
                    in routes/issue.js. */}
                <SortableTh field="project" {...sorter.props}>Project</SortableTh>
                <SortableTh field="sourceType" {...sorter.props}>Source</SortableTh>
                <SortableTh field="status" {...sorter.props}>Status</SortableTh>
                <SortableTh field="priority" {...sorter.props}>Priority</SortableTh>
                <SortableTh field="createdByName" {...sorter.props}>Created By</SortableTh>
                {/* Responsible is a LIST of people: ordered by the first name
                    the cell shows, nobody responsible last (routes/issue.js). */}
                <SortableTh field="responsibleName" {...sorter.props}>Responsible</SortableTh>
                <SortableTh field="createdAt" numeric {...sorter.props}>Created</SortableTh>
                <SortableTh field="dueDate" numeric style={{ width: 150 }} {...sorter.props}>Due</SortableTh>
                <SortableTh field="updatedAt" numeric {...sorter.props}>Last Modified</SortableTh>
                {/* Who touched it last, rather than how many objects it
                    carries: the count answered a question nobody was
                    asking, and "who has this now" is the first thing
                    read off a list of open issues. */}
                <SortableTh field="updatedByName" style={{ width: 150 }} {...sorter.props}>Updated by</SortableTh>
                {/* Read from the sign-off toggles, not stored on the issue -
                    the server judges the whole list to order it by date. */}
                <SortableTh field="ownerSignOff" numeric style={{ width: 150 }} {...sorter.props}>Signed off by owner/GC</SortableTh>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            {(!apiLoading || loadedOnce.current) && issues?.length !== 0 ? (
              <tbody className="no-wrap">
                {issues.slice(0, data.current?.limit).map((item, index) => (
                  <tr key={item._id} style={settledStyle(item)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(String(item._id))}
                        onChange={() => toggleIssue(item._id)}
                      />
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {item.displayId
                        ? <a href={issueHref(item)} target="_blank" rel="noopener noreferrer" style={{ color: T.navyD }}>
                            {issueLabel(item)}
                          </a>
                        : "—"}
                    </td>
                    <td style={{ textAlign: "center" }} data-testid={`approved-${item._id}`}>
                      <Tooltip title={item.approvedToResolve
                        ? "Approved to resolve - cleared to work on"
                        : "Not approved to resolve yet"}>
                        <span
                          aria-label={item.approvedToResolve ? "Approved to resolve" : "Not approved to resolve"}
                          style={{
                            display: "inline-block", width: 11, height: 11, borderRadius: "50%",
                            background: item.approvedToResolve ? "#12b76a" : "#f04438",
                          }}
                        />
                      </Tooltip>
                    </td>
                    <td>
                      <div
                        style={{
                          width: "80px",
                          height: "60px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#F3F4F5",
                          borderRadius: "6px",
                          overflow: "hidden",
                          cursor: "pointer",
                        }}
                        onClick={() => openIssueViewer(item)}
                      >
                        {previewSrc(item) ? (
                          <img
                            src={previewSrc(item)}
                            alt="Preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <FileImageOutlined style={{ fontSize: 24, color: T.sub }} />
                        )}
                      </div>
                    </td>
                    <td>
                      <a
                        className="text-primary"
                        href={issueHref(item)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Opens in a new tab"
                      >
                        {item.title}
                      </a>
                    </td>
                    <td>{item.projectId?.title || "-"}</td>
                    <td>{getSourceTypeBadge(item.sourceType)}</td>
                    <td>
                      {/* Same STATUS / PRIORITY / ASSIGNED-TO controls as the
                          resolve page (components/Issue/IssueQuickActions). */}
                      {item._settled ? (
                        <SettledBadge row={item} style={{ marginLeft: 0 }} />
                      ) : ["resolved", "abandoned"].includes(item.status) ? (
                        getStatusBadge(item.status)
                      ) : (
                        <IssueStatusSelect
                          issue={item}
                          onChanged={(patch) => patchIssueRow(item._id, patch)}
                          style={{ width: 120 }}
                        />
                      )}
                    </td>
                    <td>
                      <IssuePrioritySelect
                        issue={item}
                        onChanged={(patch) => patchIssueRow(item._id, patch)}
                        style={{ width: 100 }}
                      />
                    </td>
                    <td>{item.createdBy?.name || "-"}</td>
                    <td>
                      <IssueAssignSelect
                        issue={item}
                        compact
                        onChanged={(patch) => patchIssueRow(item._id, patch)}
                      />
                      {item.responsibleUsers && item.responsibleUsers.length > 1 ? (
                        <Tooltip
                          title={item.responsibleUsers.map(u => u.name).join(", ")}
                        >
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                            <span style={{ fontSize: 10.5, color: T.muted }}>also:</span>
                            {item.responsibleUsers.slice(1, 3).map((user, idx) => (
                              <Tag
                                key={idx}
                                style={{ margin: 0, fontSize: 11, cursor: user?._id ? "pointer" : "default", background: T.wash, borderColor: "#D4E3EC", color: T.navyD }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (user?._id) history.push(`/analytics/user/${user._id}`);
                                }}
                                title={user?._id ? "View analytics" : undefined}
                              >
                                {user.name?.split(" ")[0] || user.email?.split("@")[0]}
                              </Tag>
                            ))}
                            {item.responsibleUsers.length > 3 && (
                              <Tag color="default" style={{ margin: 0, fontSize: 11 }}>
                                +{item.responsibleUsers.length - 3}
                              </Tag>
                            )}
                          </div>
                        </Tooltip>
                      ) : null}
                    </td>
                    <td>
                      {item.createdAt ? (
                        <Tooltip title={moment(item.createdAt).format("MMMM D, YYYY h:mm A")}>
                          {moment(item.createdAt).format("MMM D, YYYY")}
                        </Tooltip>
                      ) : "—"}
                    </td>
                    <td>
                      {/* Editable in place, like STATUS / PRIORITY / RESPONSIBLE
                          beside it: a due date is usually agreed while
                          reading down the list, not by opening every issue. */}
                      <IssueDueDateSelect
                        issue={item}
                        onChanged={(patch) => patchIssueRow(item._id, patch)}
                      />
                    </td>
                    <td>
                      <Tooltip title={moment(item.updatedAt).format("MMMM D, YYYY h:mm A")}>
                        {moment(item.updatedAt).fromNow()}
                      </Tooltip>
                    </td>
                    <td>{renderLastEditor(item)}</td>
                    <td>{renderOwnerSignOff(item)}</td>
                    <td>
                      <Dropdown menu={{ items: getActionMenuItems(item) }} trigger={["click"]}>
                        <button className="border-none bg-transparent cursor-pointer">
                          <EllipsisOutlined style={{ fontSize: 20 }} />
                        </button>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <td colSpan={16} style={{ textAlign: "center", padding: "40px" }}>
                    {apiLoading ? (
                      "Loading..."
                    ) : (
                      <div>
                        <IssueIcon />
                        <p className="mt-2 mb-1" style={{ fontSize: 16, fontWeight: 500, color: T.ink }}>
                          No issues found
                        </p>
                        <p style={{ color: T.muted, marginBottom: 16 }}>
                          {canCreate ? "Create your first issue to get started" : "No issues yet"}
                        </p>
                        {canCreate && (
                        <button
                          onClick={() =>
                            history.push(
                              projectFilter
                                ? `/issue/new?projectId=${projectFilter}`
                                : "/issue/new"
                            )
                          }
                          className="px-4 py-2"
                          style={{ background: T.orange, color: "#fff", border: "none" }}
                        >
                          Create Issue
                        </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            )}
          </table>
        </div>
        )}

        {layout === "table" && (
        // .pagination-css is position:absolute; bottom:0 - give the wrapper a
        // real height so the bar sits BELOW the table instead of hanging over
        // its last row.
        <div className="asset-pagination-div mx-auto" style={{ height: 46, marginTop: 6 }}>
          <Pagination
            totalRecords={totalRecords}
            totalPage={totalPage}
            page={page + 1}
            onPageChange={(pageNo) => setPage(pageNo - 1)}
            onLimitChange={(count) => {
              data.current = { ...data.current, limit: count };
              if (page === 0) {
                getIssuesAPI(data);
              } else {
                setPage(0);
              }
            }}
            limit={data.current?.limit}
          />
        </div>
        )}
      </div>

      {/* Bulk assign responsible to the filtered list */}
      <Modal title={`${bulkMode === "remove" ? "Remove responsible" : "Assign responsible"} - ${bulkCount ?? 0} issue(s) in the current list`} open={bulkOpen} onCancel={() => setBulkOpen(false)}
        onOk={applyBulkAssign} okText={bulkMode === "remove" ? "Remove from all" : "Apply to all"} okButtonProps={bulkMode === "remove" ? { danger: true } : undefined} confirmLoading={bulkWorking} destroyOnClose>
        <p style={{ fontSize: 13, color: T.muted }}>
          {bulkMode === "remove"
            ? "Takes these people off every issue matching the current search / filters (all pages). They stop getting those issues' notifications and emails; nobody is emailed about the removal."
            : "Applies to every issue matching the current search / filters (all pages). Each person gets one email listing the issues."}
        </p>
        <div style={{ marginBottom: 10, display: "flex", gap: 14, fontSize: 12 }}>
          {[["add", "Add people"], ["replace", "Replace existing people"], ["remove", "Remove people"]].map(([v, l]) => (
            <label key={v} style={{ cursor: "pointer" }}>
              <input type="radio" name="bulkMode" checked={bulkMode === v} onChange={() => { setBulkMode(v); setBulkPeople([]); if (v === "remove") setBulkCompany(undefined); }} style={{ marginRight: 6 }} />{l}
            </label>
          ))}
        </div>
        {bulkMode !== "remove" && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>Partner / contractor (optional)</div>
            <AntSelect allowClear showSearch optionFilterProp="label" style={{ width: "100%" }} placeholder="Partner company" value={bulkCompany}
              options={bulkRoster.map((g) => ({ value: g.companyId, label: groupLabel(g) }))} onChange={(v) => { setBulkCompany(v); setBulkPeople([]); }} />
          </div>
        )}
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>{bulkMode === "remove" ? "People to remove (one or many)" : "Responsible people (one or many)"}</div>
          {/* The shared picker (components/PeoplePicker): the chosen
              partner's people, or everyone, grouped under company - trade.
              Removing works on accounts only, so a contact with no account
              is shown greyed out there with the reason; adding one goes
              through bulk-responsibles' email path as before. */}
          <PeoplePicker mode="multiple"
            placeholder={bulkMode === "remove" ? "People with an account on this project" : "People (contacts without an account get one)"} value={bulkPeople}
            roster={bulkCompany ? bulkRoster.filter((x) => String(x.companyId) === String(bulkCompany)) : bulkRoster}
            personValue={bulkPersonValue}
            noAccount={bulkMode === "remove" ? "disable" : "allow"}
            executives={bulkMode === "remove" ? "allow" : "disable"}
            onChange={setBulkPeople} />
        </div>
      </Modal>

      <Modal title={`Set fields - ${fieldsCount ?? 0} issue(s) in the current list`} open={fieldsOpen} onCancel={() => setFieldsOpen(false)}
        onOk={applyBulkFields} okText="Apply to all" confirmLoading={fieldsWorking} destroyOnClose>
        <p style={{ fontSize: 13, color: T.muted }}>Applies to the ticked issues, or to every issue matching the current search / filters (all pages) when none are ticked. A field left untouched stays as it is on each issue.</p>
        <BulkContractorRateModal
        open={rateOpen}
        onClose={() => setRateOpen(false)}
        issueIds={selectedIssueIds}
        contractors={contractorOpts}
        projectId={projectFilter}
        onDone={(r, what) => {
          message.success(`${what.companyName || "That contractor"} priced at ${what.rate}/h on ${r.saved || what.count} issue${(r.saved || what.count) === 1 ? "" : "s"}`);
          dispatch(refreshIssueLists());
        }}
      />
      <BulkSetFieldsForm bulkFields={bulkFields} setBulkFields={setBulkFields} projectFilter={projectFilter}
          disciplines={fieldsDisciplines} roster={fieldsRoster} tags={fieldsTags} activities={fieldsActivities} />
      </Modal>

      {/* Email delivery test */}
      <Modal
        title={testRow ? `Test email delivery — ${testRow.displayId ? `${issueLabel(testRow)}` : testRow.title}` : ""}
        open={!!testRow}
        onCancel={() => setTestRow(null)}
        onOk={canShareIssue ? sendDeliveryTest : requestShareAccess}
        okText={canShareIssue ? "Send test email" : (accessRequested ? "Request sent" : "Request access")}
        okButtonProps={{ disabled: !canShareIssue && accessRequested }}
        confirmLoading={testWorking || accessRequestWorking}
        destroyOnClose
      >
        {canShareIssue ? (
          <>
            <p style={{ fontSize: 13, color: T.muted }}>
              Sends this issue's email exactly as a recipient would get it, marked as a delivery test, to one address of your choice.
              Nothing is shared or changed on the issue - no link is enabled and nobody is added.
            </p>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Send to</div>
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email address" onPressEnter={sendDeliveryTest} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Permission the real link would carry</div>
              <AntSelect style={{ width: 260 }} value={testPermission} onChange={setTestPermission}
                options={[
                  { value: "edit", label: "Can update status + comment" },
                  { value: "comment", label: "Can comment only" },
                  { value: "view", label: "View only" },
                ]} />
            </div>
            {testResult && (
              <div style={{ fontSize: 12.5, padding: 8, borderRadius: 6, background: testResult.ok ? "#f6ffed" : "#fff1f0", border: `1px solid ${testResult.ok ? "#b7eb8f" : "#ffa39e"}` }}>
                {testResult.ok ? <>Handed to the mail server for <b>{testResult.email}</b>{testResult.delivery?.response ? <> - server said: <code>{String(testResult.delivery.response).slice(0, 120)}</code></> : null}{testResult.delivery?.messageId ? <div style={{ color: T.muted }}>message id {testResult.delivery.messageId}</div> : null}<div style={{ color: T.muted }}>Check the inbox (and spam). Delivery status and bounces are in Reports & notifications / Email delivery.</div></>
                  : <>Failed for <b>{testResult.email}</b>{testResult.error ? `: ${testResult.error}` : (testResult.failed || []).map((f) => `: ${f.err}`).join("")}</>}
              </div>
            )}
          </>
        ) : (
          <p style={{ fontSize: 13, color: T.muted }}>
            Testing email delivery for an issue needs the <b>issueShare</b> permission (the same one that emails issue links).
            Ask your company admin - this sends them an access request for it.
          </p>
        )}
      </Modal>

      {/* Email issue link (no-login guest access) */}
      <Modal
        title={emailLinkRow ? `Email link — ${emailLinkRow.title}` : ""}
        open={!!emailLinkRow}
        onCancel={() => setEmailLinkRow(null)}
        onOk={sendEmailLink}
        okText="Send"
        confirmLoading={emailLinkWorking}
        destroyOnClose
      >
        <p style={{ fontSize: 13, color: T.muted }}>
          Recipients get an email with a direct link — no account needed.
          Depending on access, they can comment, attach screenshots and
          update the issue's status, which updates the master plan and
          gantt automatically.
        </p>
        <div style={{ marginBottom: 10 }}>
          <AntSelect
            mode="tags"
            style={{ width: "100%" }}
            placeholder="Recipients (project users, partner contacts, or any email)"
            value={emailLinkEmails}
            onChange={setEmailLinkEmails}
            options={emailLinkOptions}
            filterOption={filterPeopleOption}
            optionRender={(o) => (o.data && o.data.person ? renderPeopleOption(o) : o.label)}
            tokenSeparators={[",", " "]}
          />
        </div>
        <AntSelect
          style={{ width: 220 }}
          value={emailLinkPermission}
          onChange={setEmailLinkPermission}
          options={[
            { value: "edit", label: "Can update status + comment" },
            { value: "comment", label: "Can comment only" },
            { value: "view", label: "View only" },
          ]}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Issue"
        open={deleteModalVisible}
        onOk={handleDeleteIssue}
        onCancel={() => {
          setDeleteModalVisible(false);
          setIssueToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete "<strong>{issueToDelete?.title}</strong>"?
        </p>
        <p style={{ color: T.muted, fontSize: 14 }}>
          This action cannot be undone. All annotations and collaborator access will be removed.
        </p>
      </Modal>

      <IssueDiffModal
        visible={diffModalVisible}
        issue={diffModalIssue}
        onClose={() => {
          setDiffModalVisible(false);
          setDiffModalIssue(null);
        }}
        onStatusChanged={(newStatus) => {
          if (diffModalIssue?._id) {
            setIssues(prev => prev.map(m =>
              m._id === diffModalIssue._id ? { ...m, status: newStatus } : m
            ));
          }
        }}
      />

      {projectFilter && (
        <ShareResourceModal
          type="issues"
          id={projectFilter}
          projectId={projectFilter}
          title={projects.find((p) => String(p._id) === String(projectFilter))?.title || ""}
          open={sharing}
          onClose={() => setSharing(false)}
        />
      )}
      <OrphanedIssuesDrawer
        open={orphansOpen}
        onClose={() => setOrphansOpen(false)}
        projectId={projectFilter}
        onChanged={() => dispatch(refreshIssueLists())}
      />
    </>
  );
}
